import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../core/context/AuthContext";
import { writeConnectorSnapshot } from "../../core/utils/connectorSnapshotCache";
import { InlineSpinner } from "./shared/SourceMessageAccordion";
import {
  remapKeyboardFocusAfterListChange,
  syncKeyboardFocusRefs,
} from "./shared/keyboardListFocus";
import ActionItemsListEmptyState from "./shared/ActionItemsListEmptyState";
import ActionItemDetailsSidebar from "./ActionItemDetailsSidebar";
import ActionItemBulkBar from "./ActionItemBulkBar";
import ActionItemRow, {
  applyPerformerResultPatch,
  buildAiResultsMapFromPerformerState,
  mergeLiveAndPersistedPerformerResult,
  overlayPerformerResultPatch,
} from "./ActionItemRow";
import { PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT } from "./performerGmailOAuth";
import { withCachedCrmWrittenFields } from "./shared/AIDraftComponents";
import config from "../../core/config";
import {
  markAiAssistSettingsShown,
  resetPerformerOnboardingLocalState,
} from "./shared/performerAssistGate";
import {
  CAPTURE_WORKFLOW_ID,
  CAPTURE_WORKFLOW_NAME,
  ENABLE_ACTION_ITEMS_BULK_SELECT,
  isManualActionItem,
  resolveActionItemPageUrl,
  MANUAL_WORKFLOW_FILTER_ID,
  filterItemsByWorkflowQuery,
  filterItemsByMeetingWindow,
  resolveActionItemSourceFilterId,
  ACTION_ITEMS_MEETING_WINDOW_MINUTES,
  buildMeetingSourceOptions,
  filterItemsExcludingWorkflowIds,
  filterItemsByEventDateRange,
  filterItemsByAgingFlag,
  isAgingFilterActiveFromSearch,
  buildActionItemsListPath,
  ACTION_ITEMS_MEETING_FILTER_QUERY_PARAM,
  ACTION_ITEMS_MEETING_EXCLUDE_QUERY_PARAM,
  ACTION_ITEMS_LIST_PATH,
  readManualActionItemsModePreferred,
  readPasteTranscriptHintDismissed,
  persistPasteTranscriptHintDismissed,
  clearPasteTranscriptHintDismissed,
  shouldRedirectEmptyAccountToConnectOnboarding,
  isPasteTranscriptHintDeepLink,
  isFirstPullFromOnboardingDeepLink,
  persistFirstPullFromOnboardingSeen,
  readFirstPullFromOnboardingSeen,
  PASTE_TRANSCRIPT_HINT_QUERY_PARAM,
  buildCatchUpAutomatablePath,
  readCatchUpDismissedSummaryId,
  persistCatchUpDismissedSummaryId,
  readAgingBannerDismissedCount,
  persistAgingBannerDismissedCount,
  ASSIST_FILTER_AI_READY,
  isAiReadyAssistFilterActive,
  parseAssistFilterSinceAtMs,
  filterItemsByAssistFilter,
  TRANSCRIPT_PASTE_MAX_CHARS,
  ACTION_ITEM_SORT_DEFAULT,
  ACTION_ITEM_SORT_OPTIONS,
  insertActionItemInViewSort,
  filterItemsByStarred,
  isStarredFilterActiveFromSearch,
  sortActionItemsForView,
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  ACTION_ITEMS_SETTINGS_TAB_GENERAL,
  buildActionItemsSettingsPath,
  pickActionItemsListFilterSearch,
  isActionItemsAiSettingsDeepLink,
  isActionItemsSettingsDeepLink,
  isPerformerOnboardingResetDeepLink,
  parseActionItemsSettingsApp,
  parseActionItemsSettingsTab,
  PERFORMER_ONBOARDING_RESET_QUERY_PARAM,
  persistExtensionOpenedTab,
  resolveDeepLinkItem,
  pendingSourceLoadMatchesDetail,
  consumePostSetupWorkflowFocus,
  tryCloseExtensionOpenedTab,
  getItemStableId,
  getItemSidebarKey,
  resolveActionItemMeetingSubject,
  formatMeetingLabelForDisplay,
  resolveMeetingAddAnchor,
  buildMeetingAddContext,
  formatMeetingAddContextHintLabel,
  MEETING_ADD_UNLINKED_HINT_LABEL,
  MEETING_ADD_RELINK_ACTION_LABEL,
} from "./actionItemHelpers";
import { resolveWorkflowLookbackDays } from "./shared/businessDigestSetup";
import VoiceActionItemCapture from "./VoiceActionItemCapture";
import ActionItemAddPlusIcon from "./shared/actionItemAddPlusIcon";
import { ActionItemDeleteIcon } from "./shared/actionItemArchiveIcons";
import ActionItemCreateFeedback from "./shared/ActionItemCreateFeedback";
import UndoToast from "./shared/UndoToast";
import { useIdleConnectorRefresh } from "./shared/useIdleConnectorRefresh";
import ActionItemsCatchUpBanner from "./shared/ActionItemsCatchUpBanner";
import ActionItemsAgingBanner from "./shared/ActionItemsAgingBanner";
import UsageLimitBanner from "./shared/UsageLimitBanner";
import AiCreditsToppedUpBanner from "./shared/AiCreditsToppedUpBanner";
import { consumeAiCreditsTopupNoticeIfReady } from "./shared/aiCreditsTopupNotice";
import {
  ACTION_ITEMS_LIST_NOTICE,
  resolveActionItemsListNotice,
} from "./shared/resolveActionItemsListNotice";
import { useAiCreditsUsageOptional } from "./shared/AiCreditsUsageContext";
import ActionItemsFilterHoverMenu from "./shared/ActionItemsFilterHoverMenu";
import ActionItemsSortHoverMenu from "./shared/ActionItemsSortHoverMenu";
import ActionItemsArchiveHoverMenu from "./shared/ActionItemsArchiveHoverMenu";
import { ActionItemsExpandableSearch } from "./shared/ActionItemsSearchToggle";
import {
  eventMatchesBinding,
  isTypingTarget,
} from "./shared/keyboardShortcuts";
import { useResolvedKeyboardShortcutBindings } from "./shared/useKeyboardShortcutsPreference";
import {
  buildActionItemsStatusStats,
  useArchivedTabVisiblePreference,
} from "./shared/actionItemsArchivedTabPreference";
import { createActionItemFromManualText } from "./shared/createActionItemFromManualText";
import {
  runOptimisticActionItemStatusChange,
  runOptimisticBulkActionItemStatusChange,
} from "./shared/optimisticActionItemStatus";
import {
  CREATE_STATUS_PHASE_CREATING,
  CREATE_STATUS_PHASE_ERROR,
} from "./shared/actionItemCreatePhases";
import ActionItemDueDateControl from "./shared/ActionItemDueDateControl";
import {
  getActionItemPerformerConfig,
  resetActionItemPerformerOnboarding,
  getActionItemsPerformerState,
  getActionItemsCatchUpSummary,
  acknowledgeActionItemsCatchUp,
  getGmailStatus,
  getRunInputMessageContent,
  getRunInputMessages,
  isGmailReadyForPerformer,
  updateActionItemStatusForItem,
  updateActionItemDueDateForItem,
  updateActionItemPinnedForItem,
  updateActionItemNoteForItem,
  updateActionItemTitleForItem,
  updateActionItemCategoryForItem,
  updateActionItemAccountForItem,
  patchActionItemPerformerConfig,
  buildCalendarPrefillUrl,
  getCalendarEventForItem,
  peekCachedCalendarEvent,
  extractActionItemsFromMeetingTranscript,
} from "../businessModeService";
import {
  appendLabelToActionItemCategoryCatalog,
  buildActionItemAccountSuggestions,
  buildActionItemsCategoryFilterOptions,
  CATEGORY_FILTER_ALL,
  filterItemsByCategoryFilter,
  getActionItemCategory,
  notifyActionItemCategoryCatalogUpdated,
  resolveActionItemCategoryCatalog,
} from "./actionItemCategories";
import ActionItemsActiveFilterBar from "./shared/ActionItemsActiveFilterBar";
import ActionItemsEventDateFilter from "./shared/ActionItemsEventDateFilter";
import { renderUsageOrError } from "./shared/UsageLimitBanner";
import {
  isWorkflowFirstRunComplete,
  isWorkflowInFirstRunSetup,
  isWorkflowSyncingActionItems,
  persistFirstRunStableWorkflowId,
  readFirstRunStableWorkflowIds,
  STABLE_DIGEST_POLLS_FOR_COMPLETE,
} from "./shared/workflowStatus";
import { useActionItemBulkSelect } from "./shared/useActionItemBulkSelect";
import { useActionItemsSplitResize } from "./shared/useActionItemsSplitResize";
import { useActionItemsSidebar } from "./ActionItemsSidebarContext";
import { useDesktopCaptureFeedback } from "./shared/useDesktopCaptureFeedback";
import { resizeAutoGrowTextarea } from "./shared/autoGrowTextarea";
import {
  dismissRowRunError,
  nextRowRunEntry,
  resolveItemActiveAiProgress,
} from "./shared/performerProgress";
import { useSingleItemPerformerRun } from "./shared/useSingleItemPerformerRun";
import ActionItemsFirstPullScreen from "./shared/ActionItemsFirstPullScreen";
import { DEFAULT_LOOKBACK_DAYS } from "./shared/lookbackPeriod";

const STATUS_PENDING = "pending";
const STATUS_DONE = "done";
const STATUS_IGNORE = "ignore";
const STATUS_DELETED = "deleted";

/** How long a just-added row stays flash-highlighted (see recentlyAddedKeys). */
const JUST_ADDED_HIGHLIGHT_MS = 2200;

/**
 * True when a status change removes the item from the active list tab.
 *
 * @example
 * // Input: activeFilter "pending", newStatus "ignore"
 * // Output: true
 * itemLeavesActiveFilterView("pending", "ignore");
 */
const itemLeavesActiveFilterView = (activeFilter, newStatus) => {
  if (activeFilter === "done") return newStatus !== STATUS_DONE;
  if (activeFilter === "ignored") return newStatus !== STATUS_IGNORE;
  return newStatus !== STATUS_PENDING;
};

/**
 * Pick the row above the removed item, else the one below, for sidebar focus.
 *
 * @example
 * // Input: list [A, B, C], removed B
 * // Output: A
 * resolveSidebarFallbackItem([itemA, itemB, itemC], getItemStableId, idB);
 */
const resolveSidebarFallbackItem = (list, getStableId, removedStableId) => {
  const index = list.findIndex((entry) => getStableId(entry) === removedStableId);
  if (index < 0) return null;
  if (index > 0) return list[index - 1];
  if (index < list.length - 1) return list[index + 1];
  return null;
};

/**
 * From a prior list, find the nearest still-visible neighbor after removal.
 *
 * @example
 * // Input: previous [A, B, C], visible [C], removed B
 * // Output: C (below) when A also left the view
 * resolveStillVisibleSidebarFallback(previous, visible, getItemStableId, idB);
 */
const resolveStillVisibleSidebarFallback = (
  previousList,
  visibleList,
  getStableId,
  removedStableId
) => {
  const index = previousList.findIndex((entry) => getStableId(entry) === removedStableId);
  if (index < 0) return null;
  const visibleIds = new Set(visibleList.map((entry) => getStableId(entry)));
  for (let i = index - 1; i >= 0; i -= 1) {
    const id = getStableId(previousList[i]);
    if (visibleIds.has(id)) return previousList[i];
  }
  for (let i = index + 1; i < previousList.length; i += 1) {
    const id = getStableId(previousList[i]);
    if (visibleIds.has(id)) return previousList[i];
  }
  return null;
};
const CREATE_FEEDBACK_SUCCESS_MS = 4500;
const CREATE_FEEDBACK_ERROR_MS = 6500;
const SETUP_LOADING_GRACE_MS = 2 * 60 * 1000;
const FIRST_RUN_REFRESH_INTERVAL_MS = 5000;
const LAST_WORKFLOW_STORAGE_KEY = "globalActionItemsLastWorkflowId";
const ENABLE_ACTION_ITEMS_AI = true;

const EMPTY_SIDEBAR_SOURCE_STATE = {
  sidebarSourceLoaded: false,
  sidebarWorkflowId: "",
  inputMessages: [],
  activeMessageId: "",
  activeSourceSnippet: "",
  contentCache: {},
  messagesLoading: false,
};

/**
 * Copy sidebar source UI state for per-task session cache storage.
 *
 * @example
 * // Input: { sidebarSourceLoaded: true, inputMessages: [{ message_id: "m1" }], contentCache: { m1: { content: "hi" } } }
 * // Output: shallow-cloned snapshot safe to store in a Map
 * cloneSidebarSourceSnapshot(snapshot);
 */
const cloneSidebarSourceSnapshot = (state) => ({
  sidebarSourceLoaded: Boolean(state?.sidebarSourceLoaded),
  sidebarWorkflowId: String(state?.sidebarWorkflowId || ""),
  inputMessages: Array.isArray(state?.inputMessages) ? [...state.inputMessages] : [],
  activeMessageId: String(state?.activeMessageId || ""),
  activeSourceSnippet: String(state?.activeSourceSnippet || ""),
  contentCache:
    state?.contentCache && typeof state.contentCache === "object"
      ? { ...state.contentCache }
      : {},
  messagesLoading: Boolean(state?.messagesLoading),
});

/**
 * Merge row-reported and sidebar-initiated performer run state for one item.
 *
 * @example
 * // Input
 * mergePerformerRunStates(
 *   { loading: true, progress: { steps: [{ label: "Drafting", status: "running" }] } },
 *   { loading: false, progress: { steps: [] }, result: null, run: () => {} }
 * );
 * // Output
 * { loading: true, progress: { steps: [{ label: "Drafting", status: "running" }] }, result: null, run: fn }
 */
const mergePerformerRunStates = (rowState = {}, detailState = {}) => ({
  loading: Boolean(rowState.loading || detailState.loading),
  progress: detailState.progress?.steps?.length
    ? detailState.progress
    : rowState.progress || { steps: [] },
  // A sidebar Try Again clears detail.result to null while loading. Falling
  // back to the row's previous parked/draft result would flash that old
  // output until the new classification arrives.
  result: detailState.loading
    ? detailState.result || null
    : detailState.result || rowState.result || null,
  crmSideEffect: detailState.loading
    ? detailState.crmSideEffect || null
    : detailState.crmSideEffect || rowState.crmSideEffect || null,
  error: detailState.error || rowState.error || "",
  run: detailState.run || null,
});

/**
 * In-session Gmail send/edit patch for one action item.
 *
 * @example
 * resolveAiResultPatch({ "w::k::f": { gmail_sent_at: "2026-08-20T10:00:00Z" } }, { action_item: "Follow up" }, "w::k::f");
 * // { gmail_sent_at: "2026-08-20T10:00:00Z" }
 */
const resolveAiResultPatch = (patches, item, stableId = "") => {
  const map = patches && typeof patches === "object" ? patches : {};
  const id = String(stableId || "").trim();
  const text = String(item?.action_item || "").trim();
  return (id && map[id]) || (text && map[text]) || null;
};

/**
 * Stable order for run input / citation messages when timestamps tie or are null.
 *
 * @example
 * // Input
 * compareRunInputMessagesById({ message_id: "m2" }, { message_id: "m10" });
 * // Output: negative (string "m10" < "m2" in localeCompare — actually "m10".localeCompare("m2") is negative because "1" < "2" at second char)
 */
const compareRunInputMessagesById = (a, b) =>
  String(a.message_id || "").localeCompare(String(b.message_id || ""));

/**
 * Callout above the add field when the user enters via "Not ready? Add tasks manually instead".
 *
 * @example
 * // Input: onDismiss called when user clicks ×
 * // Output: parent hides hint and removes input highlight
 */
const PasteTranscriptHint = ({ onDismiss }) => (
  <div className="action-items-paste-hint" role="status" aria-live="polite">
    <div className="action-items-paste-hint__bubble">
      <p className="action-items-paste-hint__text">Type your first task here</p>
      <button
        type="button"
        className="action-items-paste-hint__dismiss"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
    <svg
      className="action-items-paste-hint__curve"
      width="52"
      height="34"
      viewBox="0 0 52 34"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 2 C10 14, 14 24, 24 30"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M24 30 L18 28 M24 30 L22 24"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

/**
 * Compact callout above the add field for meeting-linked manual adds.
 *
 * @example
 * // Linked — new tasks join the active meeting filter
 * <MeetingAddHint mode="linked" label="Adds to this meeting · Tue, Jul 28 · 10:45 AM" onDismiss={...} />
 *
 * @example
 * // Unlinked — manual adds use Added-by-you only
 * <MeetingAddHint mode="unlinked" label="Added by you — won't show in this meeting" onEnable={...} />
 */
const MeetingAddHint = ({ mode = "linked", label, onDismiss, onEnable }) => (
  <div className="action-items-add-hint" role="status" aria-live="polite">
    <div
      className={[
        "action-items-add-hint__bubble",
        mode === "unlinked" ? "action-items-add-hint__bubble--unlinked" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <p className="action-items-add-hint__text">{label}</p>
      {mode === "linked" ? (
        <button
          type="button"
          className="action-items-add-hint__dismiss"
          onClick={onDismiss}
          aria-label="Add by you instead (not linked to this meeting)"
          title="Add by you instead"
        >
          ×
        </button>
      ) : (
        <button
          type="button"
          className="action-items-add-hint__action"
          onClick={onEnable}
        >
          {MEETING_ADD_RELINK_ACTION_LABEL}
        </button>
      )}
    </div>
  </div>
);

/**
 * Below this length, text always submits verbatim as one task -- no LLM
 * call, no chance of it being reworded. Above it, the text is checked for
 * multiple tasks (see GlobalAddActionItemForm's handleSubmit): a single
 * result still submits on its own, only 2+ results show a review step.
 * There is no user-facing mode for any of this -- the length is just what
 * decides whether it's worth even asking the question.
 */
const MANUAL_ACTION_ITEM_CHAR_THRESHOLD = 250;

/**
 * Inline form to create an action item from the global list.
 * New items are always created as manual captures.
 *
 * @example
 * // Input
 * <GlobalAddActionItemForm
 *   onSubmit={async (workflowId, text) => { await createActionItem({ actionItemText: text, workflowId }); }}
 *   onCancel={() => setShowAddForm(false)}
 * />
 * // Output
 * // Renders task field, voice capture, due date, Add, and optional Cancel.
 *
 * @example
 * addFormControlsRef.current.toggleVoice();
 * // Starts or stops microphone capture
 */
const GlobalAddActionItemForm = ({
  onSubmit,
  onCancel,
  showCancel = true,
  inputRef: inputRefProp,
  controlsRef = null,
  pasteHintActive = false,
  onPasteHintDismiss,
  meetingHintLinked = false,
  meetingHintUnlinked = false,
  meetingHintLabel = "",
  onMeetingHintDismiss,
  onMeetingHintEnable,
}) => {
  const [text, setText] = useState("");
  const [actionTitle, setActionTitle] = useState("");
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [voicePhase, setVoicePhase] = useState("idle");
  const [voiceError, setVoiceError] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [fieldExpanded, setFieldExpanded] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [reviewItems, setReviewItems] = useState(null);
  const internalInputRef = useRef(null);
  const voiceCaptureRef = useRef(null);

  const assignTextInputRef = useCallback(
    (node) => {
      internalInputRef.current = node;
      if (!inputRefProp) return;
      if (typeof inputRefProp === "function") {
        inputRefProp(node);
      } else {
        inputRefProp.current = node;
      }
    },
    [inputRefProp]
  );

  useEffect(() => {
    if (!controlsRef) return undefined;
    const api = {
      toggleVoice: () => voiceCaptureRef.current?.toggle?.(),
    };
    if (typeof controlsRef === "function") {
      controlsRef(api);
      return () => controlsRef(null);
    }
    controlsRef.current = api;
    return () => {
      controlsRef.current = null;
    };
  }, [controlsRef]);

  useEffect(() => {
    const el = internalInputRef.current;
    if (!el) return;
    setFieldExpanded(resizeAutoGrowTextarea(el, ""));
  }, []);

  const handleTextChange = (event) => {
    const nextValue = event.target.value;
    setText(nextValue);
    setFieldExpanded(resizeAutoGrowTextarea(event.target, nextValue));
    if (pasteHintActive && nextValue.trim()) {
      onPasteHintDismiss?.();
    }
  };

  const resetFormFields = () => {
    setText("");
    setActionTitle("");
    setVoiceTranscript("");
    setDueDate("");
    setFieldExpanded(resizeAutoGrowTextarea(internalInputRef.current, ""));
  };

  const submitSingleItem = (itemText, { actionTitle: title = "", sourceSnippet = "" } = {}) => {
    const wid = "";
    resetFormFields();
    onSubmit(wid, itemText, CAPTURE_WORKFLOW_NAME, dueDate, {
      actionTitle: title,
      sourceSnippet,
    });
    internalInputRef.current?.focus();
    if (typeof onCancel === "function") onCancel();
  };

  /**
   * Below MANUAL_ACTION_ITEM_CHAR_THRESHOLD, always verbatim -- no LLM call,
   * so there's nothing to reinterpret. Above it, check whether the text
   * actually contains more than one task; a single result is submitted
   * directly (same as verbatim, just LLM-titled), and only 2+ results ever
   * show the review list. Any extraction hiccup (including "found nothing")
   * falls back to a plain verbatim add rather than a dead end.
   *
   * @example
   * // Input: text = "Follow up with legal"
   * // Output: submitted verbatim, no LLM call
   *
   * @example
   * // Input: text = a 400-char paste describing one task
   * // Output: extraction runs, returns 1 item, submitted directly
   *
   * @example
   * // Input: text = a pasted transcript with 3 commitments
   * // Output: extraction runs, returns 3 items, review list shown
   */
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || isChecking) return;
    if (pasteHintActive) onPasteHintDismiss?.();

    if (trimmed.length <= MANUAL_ACTION_ITEM_CHAR_THRESHOLD) {
      submitSingleItem(trimmed, {
        actionTitle: String(actionTitle || "").trim(),
        sourceSnippet: String(voiceTranscript || "").trim(),
      });
      return;
    }

    setIsChecking(true);
    try {
      const { action_items: items } = await extractActionItemsFromMeetingTranscript(trimmed);
      const list = Array.isArray(items) ? items.filter((it) => it && it.action_item_text) : [];
      if (list.length === 0) {
        submitSingleItem(trimmed, { actionTitle: String(actionTitle || "").trim() });
      } else if (list.length === 1) {
        const only = list[0];
        submitSingleItem(String(only.action_item_text || trimmed).trim(), {
          actionTitle: String(only.action_title || "").trim(),
          sourceSnippet: String(only.source_snippet || "").trim(),
        });
      } else {
        setReviewItems(
          list.map((it, idx) => ({
            id: `review-${idx}`,
            title: String(it.action_title || "").trim(),
            body: String(it.action_item_text || "").trim(),
            checked: true,
          }))
        );
      }
    } catch {
      submitSingleItem(trimmed, { actionTitle: String(actionTitle || "").trim() });
    } finally {
      setIsChecking(false);
    }
  };

  const updateReviewItem = (id, patch) => {
    setReviewItems((prev) =>
      (prev || []).map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  };

  const cancelReview = () => {
    setReviewItems(null);
    internalInputRef.current?.focus();
  };

  /**
   * Add every checked review item, each as its own task -- reuses the same
   * single-item submit path the rest of the form uses, just called once per
   * selected item.
   *
   * @example
   * // Input: 2 of 3 review items checked
   * // Output: submitSingleItem called twice, review list cleared
   */
  const confirmReview = () => {
    const chosen = (reviewItems || []).filter((item) => item.checked && item.body.trim());
    chosen.forEach((item) => {
      submitSingleItem(item.body.trim(), { actionTitle: item.title.trim() });
    });
    setReviewItems(null);
  };

  /**
   * Enter submits; Escape clears the draft and leaves the field
   * so page keyboard shortcuts work again.
   *
   * @example
   * // Input: focus in textarea with "Hello", press Escape
   * // Output: fields cleared, textarea blurred
   * // Input: focus in empty textarea, press Escape
   * // Output: textarea blurred (shortcuts active)
   */
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
      return;
    }
    if (event.key !== "Escape") return;
    event.preventDefault();
    if (voicePhase === "recording") {
      voiceCaptureRef.current?.toggle?.();
    }
    if (text || actionTitle || voiceTranscript || dueDate) {
      resetFormFields();
      setVoiceError("");
    }
    internalInputRef.current?.blur();
    if (showCancel && typeof onCancel === "function") {
      onCancel();
    }
  };

  const disableForm = false;
  const voiceBusy = voicePhase === "transcribing" || voicePhase === "generating";
  const isRecording = voicePhase === "recording";
  const formBusy = voiceBusy || isChecking;

  /**
   * Focus the task field; submit when text is already entered.
   *
   * @example
   * // Input: text = "", click +
   * // Output: input receives focus
   * // Input: text = "Review contract", click +
   * // Output: handleSubmit runs
   */
  const handlePlusClick = (event) => {
    internalInputRef.current?.focus();
    if (text.trim() && !disableForm && !formBusy && !isRecording) {
      handleSubmit(event);
    }
  };

  const taskPlaceholder = voiceBusy
    ? voicePhase === "transcribing"
      ? "Transcribing…"
      : "Generating task…"
    : "Describe the task…";

  const showTopRow = meetingHintLinked || meetingHintUnlinked;

  return (
    <>
      {showTopRow ? (
        <div className="action-items-add-form__top-row">
          <MeetingAddHint
            mode={meetingHintLinked ? "linked" : "unlinked"}
            label={meetingHintLabel}
            onDismiss={onMeetingHintDismiss}
            onEnable={onMeetingHintEnable}
          />
        </div>
      ) : null}
    {reviewItems ? (
      <div className="action-items-review" role="region" aria-label="Review tasks before adding">
        <p className="action-items-review__heading">
          {reviewItems.filter((item) => item.checked).length} of {reviewItems.length} tasks
          selected — this text split into more than one, review before adding.
        </p>
        <div className="action-items-review__list">
          {reviewItems.map((item) => (
            <div key={item.id} className="action-items-review__row">
              <input
                type="checkbox"
                checked={item.checked}
                onChange={(event) => updateReviewItem(item.id, { checked: event.target.checked })}
                aria-label={`Include "${item.title}"`}
              />
              <div className="action-items-review__fields">
                <input
                  className="action-items-review__title"
                  value={item.title}
                  onChange={(event) => updateReviewItem(item.id, { title: event.target.value })}
                  aria-label="Task title"
                />
                <textarea
                  className="action-items-review__body"
                  rows={2}
                  value={item.body}
                  onChange={(event) => updateReviewItem(item.id, { body: event.target.value })}
                  aria-label="Task description"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="action-items-review__actions">
          <button type="button" className="action-items-review__cancel" onClick={cancelReview}>
            Cancel
          </button>
          <button
            type="button"
            className="action-items-review__confirm"
            onClick={confirmReview}
            disabled={!reviewItems.some((item) => item.checked)}
          >
            Add {reviewItems.filter((item) => item.checked).length} tasks
          </button>
        </div>
      </div>
    ) : (
    <form
      className={[
        "action-items-add-form",
        fieldExpanded ? "action-items-add-form--multiline" : "",
        pasteHintActive ? "action-items-add-form--paste-hint" : "",
        meetingHintLinked ? "action-items-add-form--meeting-linked" : "",
        meetingHintUnlinked ? "action-items-add-form--meeting-unlinked" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onSubmit={handleSubmit}
    >
      <div
        className={[
          "action-items-add-form__input-wrap",
          isRecording ? "action-items-add-form__input-wrap--recording" : "",
          fieldExpanded ? "action-items-add-form__input-wrap--multiline" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className="action-items-add-form__submit action-item-add-plus-btn action-items-add-form__plus"
          onClick={handlePlusClick}
          disabled={disableForm || formBusy || isRecording}
          aria-label="Add a task"
        >
          <ActionItemAddPlusIcon />
        </button>
        {isChecking ? (
          <div className="action-items-add-form__processing" role="status" aria-live="polite">
            <span className="action-items-add-form__mic-spinner" aria-hidden="true" />
            <span>Checking for multiple tasks…</span>
          </div>
        ) : (
          <textarea
            ref={assignTextInputRef}
            rows={1}
            className="action-items-add-form__input action-items-add-form__textarea"
            placeholder={taskPlaceholder}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            disabled={disableForm || voiceBusy || isRecording}
            maxLength={TRANSCRIPT_PASTE_MAX_CHARS}
            aria-label="Task description"
          />
        )}
      </div>
      <ActionItemDueDateControl
        className="action-items-add-form__due-wrap"
        value={dueDate}
        onChange={setDueDate}
        disabled={disableForm || isRecording}
      />
      <VoiceActionItemCapture
        ref={voiceCaptureRef}
        className="action-items-add-form__mic-end"
        disabled={disableForm}
        onPhaseChange={setVoicePhase}
        onError={setVoiceError}
        onDraft={({ transcript, actionTitle: title, actionItemText }) => {
          setVoiceError("");
          setVoiceTranscript(String(transcript || "").trim());
          setActionTitle(String(title || "").trim());
          setText(String(actionItemText || "").trim());
          internalInputRef.current?.focus();
        }}
      />
      {showCancel ? (
        <button
          type="button"
          className="action-items-add-form__cancel"
          onClick={onCancel}
          aria-label="Cancel"
        >
          ✕
        </button>
      ) : null}
      {voiceError
        ? renderUsageOrError(voiceError, "p", "action-items-add-form__error")
        : null}
    </form>
    )}
    </>
  );
};


/** Matches BusinessMode.css: dock details panel beside the list from this width up. */
const ACTION_ITEMS_DETAILS_DOCK_MIN_WIDTH_PX = 901;

/**
 * True when the details panel should sit in the right column (wide layout).
 *
 * @example
 * // Input: viewport 1200px → true
 * // Input: viewport 800px → false (panel renders under the selected row)
 */
const useDockedActionItemDetails = () => {
  const mediaQuery = `(min-width: ${ACTION_ITEMS_DETAILS_DOCK_MIN_WIDTH_PX}px)`;
  const [docked, setDocked] = useState(
    () => typeof window !== "undefined" && window.matchMedia(mediaQuery).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(mediaQuery);
    const syncDocked = () => setDocked(mql.matches);
    syncDocked();
    mql.addEventListener("change", syncDocked);
    return () => mql.removeEventListener("change", syncDocked);
  }, [mediaQuery]);

  return docked;
};

/**
 * Scroll a focused row inside `.action-items-scroll-body` without moving the page.
 *
 * The landing demo clips overflow in a fixed-height frame. `scrollIntoView`
 * finds no usable ancestor there and walks up to the marketing page instead.
 *
 * @param {HTMLElement|null} row Focused action-item row.
 * @returns {void}
 * @example
 * scrollRowIntoActionItemsBody(document.querySelector(".action-item-row"));
 * // Output: list scroller.scrollTop adjusted so the row is fully visible
 */
const scrollRowIntoActionItemsBody = (row) => {
  if (!row || typeof row.closest !== "function") return;
  const scroller = row.closest(".action-items-scroll-body");
  if (!scroller) return;
  const rowRect = row.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  if (rowRect.top < scrollerRect.top) {
    scroller.scrollTop += rowRect.top - scrollerRect.top - 8;
    return;
  }
  if (rowRect.bottom > scrollerRect.bottom) {
    scroller.scrollTop += rowRect.bottom - scrollerRect.bottom + 8;
  }
};

/* ── Main component ── */
const GlobalActionItemsPage = ({
  isLandingDemo = false,
  landingDemoFocusItemKey = null,
  landingDemoHoverItemKey = null,
  landingDemoInboxAction = null,
  landingDemoPerformerAction = null,
  landingDemoInboxFilter = null,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const aiCreditsUsage = useAiCreditsUsageOptional();
  const shortcutBindings = useResolvedKeyboardShortcutBindings(user);
  const searchControlsRef = useRef(null);
  const addFormControlsRef = useRef(null);
  const [keyboardFocusIndex, setKeyboardFocusIndex] = useState(-1);
  const keyboardFocusIndexRef = useRef(-1);
  keyboardFocusIndexRef.current = keyboardFocusIndex;
  const keyboardFocusItemIdRef = useRef(null);
  const skipNextKeyboardFocusScrollRef = useRef(false);
  const [keyboardNavActive, setKeyboardNavActive] = useState(false);
  const {
    searchQuery,
    setSearchQuery,
    items,
    setItems,
    workflows,
    itemsLoading,
    itemsError,
    lastFetchedAt,
    refreshActionItemsData,
    refreshWorkflows,
    registerMutation,
    unregisterMutation,
    sourceNav,
    setCategoryCatalog,
  } = useActionItemsSidebar();

  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState("pending");
  const [updatingKey, setUpdatingKey] = useState(null);
  // Sidebar keys (see getItemSidebarKey) of rows to flash-highlight right
  // after they're added -- stays keyed on _clientKey so the highlight
  // survives the optimistic -> server-reconciled swap in
  // handleAddGlobalActionItem, not just the initial optimistic insert.
  const [recentlyAddedKeys, setRecentlyAddedKeys] = useState(() => new Set());
  const [clearingArchived, setClearingArchived] = useState(false);
  const [aiResultsByItem, setAiResultsByItem] = useState({});
  const [aiResultPatchesByItem, setAiResultPatchesByItem] = useState({});
  const [performerRunStateByItem, setPerformerRunStateByItem] = useState({});
  const performerRunStateByItemRef = useRef(performerRunStateByItem);
  performerRunStateByItemRef.current = performerRunStateByItem;
  const [gmailAuthUrl, setGmailAuthUrl] = useState("");
  const [gmailPerformerReady, setGmailPerformerReady] = useState(false);
  const [createFeedback, setCreateFeedback] = useState(null);
  // Desktop capture (Ctrl+Option+K / tray) runs in the main process, so its
  // progress reaches this banner over IPC instead of through handleAdd*.
  useDesktopCaptureFeedback(setCreateFeedback, { enabled: !isLandingDemo });
  const [showArchivedTabPref] = useArchivedTabVisiblePreference();
  // Creates still waiting on the server. A reload in that window used to drop
  // the task silently, so the browser asks before leaving while this is > 0.
  const pendingCreatesRef = useRef(0);
  useEffect(() => {
    const warnIfSaving = (event) => {
      if (pendingCreatesRef.current <= 0) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnIfSaving);
    return () => window.removeEventListener("beforeunload", warnIfSaving);
  }, []);
  const showArchivedTab = isLandingDemo ? true : showArchivedTabPref;
  const [listSortOrder, setListSortOrder] = useState(ACTION_ITEM_SORT_DEFAULT);
  const [eventDateFilterVisible, setEventDateFilterVisible] = useState(false);
  const [eventDateFrom, setEventDateFrom] = useState("");
  const [eventDateTo, setEventDateTo] = useState("");

  const handleActivateEventDateFilter = useCallback(() => {
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    setEventDateFrom(from);
    setEventDateTo(to);
    setEventDateFilterVisible(true);
  }, []);

  const handleEventDateFilterChange = useCallback((from, to) => {
    setEventDateFrom(from);
    setEventDateTo(to);
  }, []);

  const handleClearEventDateFilter = useCallback(() => {
    setEventDateFrom("");
    setEventDateTo("");
    setEventDateFilterVisible(false);
  }, []);

  const handleEnterArchivedView = useCallback(() => {
    setActiveFilter("ignored");
  }, []);

  const handleExitArchivedView = useCallback(() => {
    setActiveFilter("pending");
  }, []);

  useEffect(() => {
    if (!showArchivedTab) {
      setActiveFilter((current) => (current === "ignored" ? "pending" : current));
    }
  }, [showArchivedTab]);

  const [performerConfig, setPerformerConfig] = useState(null);
  const performerConfigRef = useRef(null);
  const mountedRef = useRef(true);
  const handledDeepLinkSearchRef = useRef("");
  const pendingSourceLoadRef = useRef(null);
  const [stableDigestPollCount, setStableDigestPollCount] = useState(0);
  const [sessionStableWorkflowIds, setSessionStableWorkflowIds] = useState(
    () => readFirstRunStableWorkflowIds()
  );
  const prevDigestCountRef = useRef(-1);
  const prevStableDigestScopeRef = useRef("");
  const manualAddInputRef = useRef(null);
  const meetingAddContextRef = useRef(null);
  const manualActionItemsMode = readManualActionItemsModePreferred();
  const [showPasteTranscriptHint, setShowPasteTranscriptHint] = useState(false);
  const [meetingLinkDismissedKeys, setMeetingLinkDismissedKeys] = useState(() => new Set());
  const showFirstPullEntry =
    isFirstPullFromOnboardingDeepLink(location.search) && !readFirstPullFromOnboardingSeen();
  const [firstPullDismissed, setFirstPullDismissed] = useState(!showFirstPullEntry);
  const [catchUpSummary, setCatchUpSummary] = useState(null);
  const [catchUpHidden, setCatchUpHidden] = useState(false);
  const [agingBannerHidden, setAgingBannerHidden] = useState(false);
  const [creditsRestoredVisible, setCreditsRestoredVisible] = useState(false);

  // One-shot "credits topped up" eligibility. Skips consume while out of
  // credits so usage-limit and restored never fight; flag stays until balance > 0.
  useEffect(() => {
    if (!user) {
      setCreditsRestoredVisible(false);
      return;
    }
    if (aiCreditsUsage?.usageLimitReached) {
      setCreditsRestoredVisible(false);
      return;
    }
    if (consumeAiCreditsTopupNoticeIfReady(user)) {
      setCreditsRestoredVisible(true);
    }
    // Intentionally keyed off primitive fields (id, credits) rather than the
    // whole `user` object, so this doesn't re-fire on unrelated user identity
    // changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.ai_credits_remaining, aiCreditsUsage?.usageLimitReached]);

  useEffect(() => {
    persistExtensionOpenedTab();
  }, [location.search]);

  useEffect(() => {
    if (!isLandingDemo) return;
    markAiAssistSettingsShown();
  }, [isLandingDemo]);

  // Legacy deep-links on action-items → redirect to dedicated settings page.
  useEffect(() => {
    if (isLandingDemo) return;
    if (!isActionItemsSettingsDeepLink(location.search)) return;
    if (!ENABLE_ACTION_ITEMS_AI) {
      tryCloseExtensionOpenedTab();
      return;
    }
    const nextTab = isActionItemsAiSettingsDeepLink(location.search)
      ? ACTION_ITEMS_SETTINGS_TAB_GENERAL
      : parseActionItemsSettingsTab(location.search);
    const nextApp = parseActionItemsSettingsApp(location.search);
    const workflowFilter = String(
      new URLSearchParams(location.search).get("workflow") || ""
    ).trim();
    navigate(
      buildActionItemsSettingsPath({
        tab: nextTab,
        app: nextApp,
        workflow: workflowFilter,
      }),
      { replace: true }
    );
  }, [isLandingDemo, location.search, navigate]);

  /**
   * Navigate to the dedicated settings page.
   *
   * @example
   * handleOpenSettings(ACTION_ITEMS_SETTINGS_TAB_GENERAL);
   *
   * @example
   * handleOpenSettings(ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS, "zoom-digest");
   */
  const handleOpenSettings = useCallback(
    (tab = ACTION_ITEMS_SETTINGS_TAB_GENERAL, appFocus = "") => {
      if (isLandingDemo || !ENABLE_ACTION_ITEMS_AI) return;
      const resolvedTab =
        tab === ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS
          ? ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS
          : ACTION_ITEMS_SETTINGS_TAB_GENERAL;
      const workflowFilter = String(
        new URLSearchParams(location.search).get("workflow") || ""
      ).trim();
      // The list filter is handed over once, as an explicit `settingsWorkflow` focus, and
      // remembered in router state so closing settings restores it. Settings never reads
      // the list's own `workflow`/`category` params.
      navigate(
        buildActionItemsSettingsPath({
          tab: resolvedTab,
          app: String(appFocus || "").trim(),
          workflow: workflowFilter,
        }),
        { state: { listSearch: pickActionItemsListFilterSearch(location.search) } }
      );
    },
    [isLandingDemo, location.search, navigate]
  );

  const settingsCacheUserKey = String(user?.id || user?.email || "").trim();
  const loadCategoryCatalog = useCallback(async () => {
    try {
      const data = await getActionItemPerformerConfig();
      setPerformerConfig(data || null);
      // Warm Settings' last-known config so its first open paints instantly too.
      if (data && !isLandingDemo) {
        writeConnectorSnapshot(settingsCacheUserKey, { performerConfig: data });
      }
    } catch (_) {
      setPerformerConfig(null);
    }
  }, [isLandingDemo, settingsCacheUserKey]);

  const handleOpenAiAssistSettings = useCallback(() => {
    markAiAssistSettingsShown();
    handleOpenSettings(ACTION_ITEMS_SETTINGS_TAB_GENERAL);
  }, [handleOpenSettings]);

  const dismissPasteTranscriptHint = useCallback(() => {
    persistPasteTranscriptHintDismissed();
    setShowPasteTranscriptHint(false);
    // Keep ?pasteTranscript=1 in the URL until the first item is created so the
    // empty-account onboarding gate does not bounce back to /onboarding.
  }, []);

  useEffect(() => {
    if (isLandingDemo) return;
    if (!isPasteTranscriptHintDeepLink(location.search)) return;
    clearPasteTranscriptHintDismissed();
    setShowPasteTranscriptHint(true);
    requestAnimationFrame(() => {
      manualAddInputRef.current?.focus();
    });
    // Keep ?pasteTranscript=1 in the URL while the escape is active so the
    // empty-account onboarding gate does not bounce back to /onboarding.
  }, [isLandingDemo, location.search]);

  // ?resetPerformerOnboarding=1 clears profile/context + local gate flags once, then strip param.
  useEffect(() => {
    if (isLandingDemo) return;
    if (!isPerformerOnboardingResetDeepLink(location.search)) return;
    let cancelled = false;
    const runReset = async () => {
      resetPerformerOnboardingLocalState();
      try {
        await resetActionItemPerformerOnboarding();
      } catch (_) {
        // still strip param and refresh local flags
      }
      if (cancelled) return;
      await loadCategoryCatalog();
      const next = new URLSearchParams(location.search);
      next.delete(PERFORMER_ONBOARDING_RESET_QUERY_PARAM);
      const qs = next.toString();
      navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
    };
    void runReset();
    return () => {
      cancelled = true;
    };
  }, [isLandingDemo, location.search, location.pathname, navigate, loadCategoryCatalog]);

  useEffect(() => {
    void loadCategoryCatalog();
  }, [loadCategoryCatalog]);


  const handleGmailPerformerReady = useCallback(() => {
    setGmailPerformerReady(true);
  }, []);

  const detailsSidebarDocked = useDockedActionItemDetails();
  const inlineDetailsSidebarRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const detailItemRef = useRef(null);
  detailItemRef.current = detailItem;
  const [sidebarSourceLoaded, setSidebarSourceLoaded] = useState(false);
  const [sidebarWorkflowId, setSidebarWorkflowId] = useState("");
  const [inputMessages, setInputMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState("");
  const [activeSourceSnippet, setActiveSourceSnippet] = useState("");
  const [contentCache, setContentCache] = useState({});
  const sourceSessionCacheRef = useRef(new Map());
  const prevDetailItemIdRef = useRef(null);
  const sourceStateSnapshotRef = useRef(EMPTY_SIDEBAR_SOURCE_STATE);
  const activeSourceItemIdRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (isLandingDemo) return;
    let cancelled = false;
    const loadGmailAuthUrl = async () => {
      try {
        const authResp = await fetch(
          `${config.BACKEND_URL}/externalauth/gmail/auth_url?purpose=performer`,
          {
            method: "GET",
            credentials: "include",
          }
        );
        if (!authResp.ok || cancelled) return;
        const authPayload = await authResp.json();
        if (!cancelled) {
          setGmailAuthUrl(authPayload.auth_url || "");
        }
      } catch (_) {
        // ignore
      }
    };
    void loadGmailAuthUrl();
    return () => {
      cancelled = true;
    };
  }, [isLandingDemo]);

  const refreshGmailPerformerReady = useCallback(async () => {
    try {
      const status = await getGmailStatus({ verify: "performer" });
      if (!mountedRef.current) return;
      setGmailPerformerReady(isGmailReadyForPerformer(status));
    } catch (_) {
      if (!mountedRef.current) return;
      setGmailPerformerReady(false);
    }
  }, []);

  useEffect(() => {
    if (isLandingDemo) return undefined;
    void refreshGmailPerformerReady();
    const onGmailConnected = () => {
      void refreshGmailPerformerReady();
    };
    window.addEventListener(PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT, onGmailConnected);
    return () => window.removeEventListener(PERFORMER_GMAIL_OAUTH_CONNECTED_EVENT, onGmailConnected);
  }, [isLandingDemo, refreshGmailPerformerReady]);

  /* ── Data loading (initial fetch lives in ActionItemsSidebarProvider) ── */

  useEffect(() => {
    if (!createFeedback?.phase) return undefined;
    if (createFeedback.phase === CREATE_STATUS_PHASE_CREATING) return undefined;
    const delayMs =
      createFeedback.phase === CREATE_STATUS_PHASE_ERROR
        ? CREATE_FEEDBACK_ERROR_MS
        : CREATE_FEEDBACK_SUCCESS_MS;
    const id = setTimeout(() => setCreateFeedback(null), delayMs);
    return () => clearTimeout(id);
  }, [createFeedback]);

  useEffect(() => {
    if (isLandingDemo) return undefined;
    if (itemsLoading) return undefined;
    let cancelled = false;
    getActionItemsPerformerState()
      .then((data) => {
        if (cancelled) return;
        const incoming = buildAiResultsMapFromPerformerState(data);
        setAiResultsByItem((prev) => {
          const next = { ...prev };
          const runState = performerRunStateByItemRef.current || {};
          const loadingIds = new Set(
            Object.entries(runState)
              .filter(([, run]) => run?.loading)
              .map(([id]) => id)
          );
          Object.entries(incoming).forEach(([key, value]) => {
            const clientId = String(value?.client_item_id || key || "").trim();
            if (loadingIds.has(key) || loadingIds.has(clientId)) return;
            const inSession = runState[clientId] || runState[key] || {};
            if (inSession.result) {
              next[key] = mergeLiveAndPersistedPerformerResult(inSession.result, value);
              return;
            }
            next[key] = value;
          });
          return next;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isLandingDemo, itemsLoading]);

  useEffect(() => {
    if (isLandingDemo) return;
    // Drop leftover post-setup focus so an earlier connect cannot re-filter the list.
    consumePostSetupWorkflowFocus();
  }, [isLandingDemo, location.search]);

  // Reconnect/manual re-sync of an already-onboarded workflow (e.g. Granola) has
  // no first-run spinner to hang a poll off of, so the board would otherwise
  // only ever catch up via the manual refresh button. Re-arm the same silent
  // poll used for first-run onboarding, gated on "a workflow past first run is
  // currently syncing" instead of "this is the user's first ever sync" — it
  // stops on its own the moment job_status leaves RUNNING.
  const hasPostFirstRunSyncInProgress = workflows.some(
    (workflow) => isWorkflowFirstRunComplete(workflow) && isWorkflowSyncingActionItems(workflow)
  );

  // Idle discovery: notice a background sync starting (or finishing) while the list is open.
  useIdleConnectorRefresh({ refreshWorkflows, enabled: !isLandingDemo });

  useEffect(() => {
    if (!hasPostFirstRunSyncInProgress) return undefined;
    const id = setInterval(() => {
      if (!document.hidden) refreshActionItemsData({ silent: true });
    }, FIRST_RUN_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasPostFirstRunSyncInProgress, refreshActionItemsData]);

  /**
   * Add a manual action item optimistically, then persist to the backend in the background.
   *
   * @example
   * // Input (from GlobalAddActionItemForm)
   * handleAddGlobalActionItem("", "Email legal about NDA", "Captured");
   * // Output
   * // Task appears in the list immediately; POST runs async and reconciles or rolls back on error.
   */
  const handleAddGlobalActionItem = useCallback((
    workflowId,
    actionItemText,
    workflowName,
    dueDate,
    {
      actionTitle = "",
      sourceSnippet = "",
      meetingFromLabel = "",
      messageSentAt = "",
      actionCategory = "",
      aiAssistType = "",
      aiAssistReason = "",
      fullSourceText = "",
    } = {}
  ) => {
    const wid = String(workflowId || "").trim();
    const dueDateStr = String(dueDate || "").trim();
    const title = String(actionTitle || "").trim();
    const snippet = String(sourceSnippet || "").trim();
    const meetingLabel = String(meetingFromLabel || "").trim();
    const sentAt = String(messageSentAt || "").trim();
    const category = String(actionCategory || "").trim();
    const assistType = String(aiAssistType || "").trim();
    const assistReason = String(aiAssistReason || "").trim();
    const fullSource = String(fullSourceText || "").trim();
    const pendingFileId = `manual::pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimisticItem = {
      workflow_id: wid || CAPTURE_WORKFLOW_ID,
      workflow_name: String(workflowName || CAPTURE_WORKFLOW_NAME).trim() || CAPTURE_WORKFLOW_NAME,
      key: "",
      file_id: pendingFileId,
      // Kept constant across the optimistic → server-reconciled swap below, so the sidebar
      // (keyed on getItemSidebarKey) doesn't remount and drop an in-progress note edit just
      // because the stable id changed when the server assigned a real key/file_id.
      _clientKey: pendingFileId,
      action_item: String(actionItemText || "").trim(),
      action_title: title,
      status: STATUS_PENDING,
      suggested_done: false,
      suggestion_reason: "",
      due_date: dueDateStr,
      meeting_from_label: meetingLabel,
      // Extraction already classified this item when category is known;
      // otherwise fall back to "Work" until the server's own classification
      // call (see create_captured_action_item) resolves on reconciliation.
      action_category: category || "Work",
      ai_assist_type: assistType || null,
      ai_assist_reason: assistReason || null,
      source_snippet: snippet,
      created_at: new Date().toISOString(),
    };
    const optimisticStableId = getItemStableId(optimisticItem);

    // Protect the placeholder before inserting it so an in-flight silent refresh
    // cannot drop the row between setItems and registerMutation.
    registerMutation(optimisticStableId);
    setItems((prev) => insertActionItemInViewSort(prev, optimisticItem, listSortOrder));
    setRecentlyAddedKeys((prev) => {
      const next = new Set(prev);
      next.add(pendingFileId);
      return next;
    });
    setTimeout(() => {
      if (!mountedRef.current) return;
      setRecentlyAddedKeys((prev) => {
        if (!prev.has(pendingFileId)) return prev;
        const next = new Set(prev);
        next.delete(pendingFileId);
        return next;
      });
    }, JUST_ADDED_HIGHLIGHT_MS);
    // Block edits (note, status, etc.) on this row until creation resolves — otherwise an
    // edit fired against the not-yet-created record races the create call: the edit's PATCH
    // targets an empty key/file_id, and even if it doesn't error outright, reconciliation
    // below overwrites action_item back to its pre-edit value when the create call lands.
    setUpdatingKey(optimisticStableId);

    pendingCreatesRef.current += 1;
    void (async () => {
      let reconciledStableId = optimisticStableId;
      try {
        const result = await createActionItemFromManualText({
          actionItemText,
          actionTitle: title,
          sourceSnippet: snippet,
          workflowId: wid,
          workflowName: String(workflowName || "").trim(),
          dueDate: dueDateStr,
          messageSentAt: sentAt,
          actionCategory: category,
          aiAssistType: assistType,
          aiAssistReason: assistReason,
          fullSourceText: fullSource,
        });
        if (!mountedRef.current) return;

        if (wid) {
          sessionStorage.setItem(LAST_WORKFLOW_STORAGE_KEY, wid);
        }

        const created = result.created && typeof result.created === "object" ? result.created : {};
        const resolvedWorkflowId =
          String(created.workflow_id || result.workflowId || wid || CAPTURE_WORKFLOW_ID).trim() ||
          CAPTURE_WORKFLOW_ID;
        const resolvedWorkflowName =
          String(created.workflow_name || result.workflowName || workflowName || CAPTURE_WORKFLOW_NAME).trim() ||
          CAPTURE_WORKFLOW_NAME;
        const createdFileId = String(created.file_id || "").trim();
        const reconciledItem = {
          workflow_id: resolvedWorkflowId,
          workflow_name: resolvedWorkflowName,
          _clientKey: pendingFileId,
          key: String(created.key || ""),
          item_id: String(created.item_id || ""),
          file_id: createdFileId.startsWith("manual::")
            ? createdFileId
            : createdFileId
              ? `manual::${createdFileId}`
              : pendingFileId,
          action_item: String(created.action_item || actionItemText),
          action_title: String(created.action_title || title || ""),
          status: String(created.status || STATUS_PENDING),
          suggested_done: false,
          suggestion_reason: "",
          due_date: String(created.due_date || dueDateStr || ""),
          meeting_from_label: String(created.meeting_from_label || meetingLabel || ""),
          action_category: String(created.action_category || category || "Work"),
          ai_assist_type: created.ai_assist_type || assistType || null,
          ai_assist_reason: created.ai_assist_reason || assistReason || null,
          source_snippet: String(created.source_snippet || snippet).trim(),
          source_gcs_url: String(created.source_gcs_url || "").trim(),
          message_sent_at: String(created.message_sent_at || sentAt || "").trim(),
          created_at: String(created.created_at || new Date().toISOString()),
        };

        const sid = getItemStableId(reconciledItem);
        reconciledStableId = sid || optimisticStableId;
        if (reconciledStableId !== optimisticStableId) {
          registerMutation(reconciledStableId);
        }

        setItems((prev) => {
          const withoutPending = prev.filter((entry) => getItemStableId(entry) !== optimisticStableId);
          const existingIndex = withoutPending.findIndex((entry) => getItemStableId(entry) === sid);
          if (existingIndex >= 0) {
            const next = withoutPending.slice();
            const existing = withoutPending[existingIndex];
            next[existingIndex] = {
              ...existing,
              ...reconciledItem,
              item_id: String(reconciledItem.item_id || existing.item_id || ""),
            };
            return sortActionItemsForView(next, listSortOrder);
          }
          return insertActionItemInViewSort(withoutPending, reconciledItem, listSortOrder);
        });

        // The optimistic placeholder's stable id (workflow_id::key::file_id) changes once the
        // server assigns a real key/file_id. If the details sidebar was open on that placeholder,
        // follow the swap explicitly — otherwise it reads as "vanished" (no more entry with the
        // old stable id) and the generic sidebar-selection fallback jumps to an unrelated
        // neighboring item, discarding whatever the user was doing (e.g. mid-typing a note).
        const openDetail = detailItemRef.current;
        if (
          openDetail &&
          (getItemStableId(openDetail) === optimisticStableId ||
            getItemSidebarKey(openDetail) === pendingFileId)
        ) {
          setDetailItem(reconciledItem);
        }

      } catch (err) {
        if (!mountedRef.current) return;
        setItems((prev) =>
          prev.filter((entry) => getItemStableId(entry) !== optimisticStableId)
        );
        setCreateFeedback({
          phase: CREATE_STATUS_PHASE_ERROR,
          message: String(err?.message || "Failed to add action item"),
        });
      } finally {
        pendingCreatesRef.current -= 1;
        if (mountedRef.current) setUpdatingKey(null);
        unregisterMutation(optimisticStableId);
        if (reconciledStableId !== optimisticStableId) {
          unregisterMutation(reconciledStableId);
        }
      }
    })();
  }, [listSortOrder, setItems, registerMutation, unregisterMutation]);

  const handleAddWithMeetingContext = useCallback(
    (workflowId, actionItemText, workflowName, dueDate, options = {}) => {
      const ctx = meetingAddContextRef.current;
      if (ctx?.linkToMeeting) {
        handleAddGlobalActionItem(
          String(ctx.workflowId || workflowId || "").trim(),
          actionItemText,
          String(ctx.workflowName || workflowName || "").trim() || CAPTURE_WORKFLOW_NAME,
          dueDate,
          {
            ...options,
            meetingFromLabel: ctx.meetingFromLabel,
            messageSentAt: ctx.messageSentAt,
          }
        );
        return;
      }
      handleAddGlobalActionItem(workflowId, actionItemText, workflowName, dueDate, options);
    },
    [handleAddGlobalActionItem]
  );

  /* ── Status update ── */
  const optimisticStatusOptions = useMemo(
    () => ({
      setItems,
      registerMutation,
      unregisterMutation,
      refreshItems: () => refreshActionItemsData({ silent: true }),
      onError: (err) => {
        if (mountedRef.current) {
          setError(String(err?.message || "Failed to update"));
        }
      },
      isMounted: () => mountedRef.current,
    }),
    [refreshActionItemsData, registerMutation, setItems, unregisterMutation]
  );

  const handleStatusChange = useCallback(
    (item, newStatus) => {
      setError("");
      void runOptimisticActionItemStatusChange({
        item,
        newStatus,
        ...optimisticStatusOptions,
      });
    },
    [optimisticStatusOptions]
  );

  const categoryCatalog = useMemo(
    () => resolveActionItemCategoryCatalog(performerConfig?.category_catalog),
    [performerConfig]
  );

  useEffect(() => {
    setCategoryCatalog(categoryCatalog);
  }, [categoryCatalog, setCategoryCatalog]);

  useEffect(() => {
    performerConfigRef.current = performerConfig;
  }, [performerConfig]);

  const accountSuggestions = useMemo(
    () => buildActionItemAccountSuggestions(items),
    [items]
  );

  const handleAccountChange = useCallback(async (item, newAccount) => {
    const stableId = getItemStableId(item);
    const previousAccount = String(item.account || "").trim();
    const normalizedAccount = String(newAccount || "").trim();
    if (normalizedAccount === previousAccount) return;

    registerMutation(stableId);
    setUpdatingKey(stableId);
    setItems((prev) =>
      prev.map((i) =>
        getItemStableId(i) === stableId
          ? { ...i, account: normalizedAccount || null }
          : i
      )
    );
    try {
      await updateActionItemAccountForItem(item, normalizedAccount);
    } catch (err) {
      if (mountedRef.current) {
        setItems((prev) =>
          prev.map((i) =>
            getItemStableId(i) === stableId ? { ...i, account: item.account } : i
          )
        );
        setError(String(err?.message || "Failed to update account"));
      }
    } finally {
      if (mountedRef.current) setUpdatingKey(null);
      unregisterMutation(stableId);
    }
  }, [setItems, registerMutation, unregisterMutation]);

  const persistCategoryToCatalog = useCallback(async (label) => {
    const savedCatalog = performerConfigRef.current?.category_catalog;
    const { appended, nextCatalog } = appendLabelToActionItemCategoryCatalog(
      savedCatalog,
      label
    );
    if (!appended) return;

    setPerformerConfig((prev) => ({ ...(prev || {}), category_catalog: nextCatalog }));
    try {
      const data = await patchActionItemPerformerConfig({ categoryCatalog: nextCatalog });
      const persistedCatalog = resolveActionItemCategoryCatalog(data?.category_catalog);
      setPerformerConfig((prev) => ({ ...(prev || {}), category_catalog: persistedCatalog }));
      notifyActionItemCategoryCatalogUpdated(persistedCatalog);
    } catch (err) {
      setPerformerConfig((prev) => ({ ...(prev || {}), category_catalog: savedCatalog ?? null }));
      if (mountedRef.current) {
        setError(String(err?.message || "Failed to save new category to settings"));
      }
    }
  }, []);

  const handleCategoryChange = useCallback(async (item, newCategory) => {
    const stableId = getItemStableId(item);
    const previousCategory = getActionItemCategory(item);
    const normalizedCategory = String(newCategory || "").trim();
    if (!normalizedCategory || normalizedCategory === previousCategory) return;

    registerMutation(stableId);
    setUpdatingKey(stableId);
    setItems((prev) =>
      prev.map((i) =>
        getItemStableId(i) === stableId
          ? { ...i, action_category: normalizedCategory }
          : i
      )
    );
    try {
      await updateActionItemCategoryForItem(item, normalizedCategory);
      await persistCategoryToCatalog(normalizedCategory);
    } catch (err) {
      if (mountedRef.current) {
        setItems((prev) =>
          prev.map((i) =>
            getItemStableId(i) === stableId
              ? { ...i, action_category: item.action_category }
              : i
          )
        );
        setError(String(err?.message || "Failed to update category"));
      }
    } finally {
      if (mountedRef.current) setUpdatingKey(null);
      unregisterMutation(stableId);
    }
  }, [persistCategoryToCatalog, setItems, registerMutation, unregisterMutation]);

  const handleDueDateChange = useCallback(async (item, newDueDate) => {
    const stableId = getItemStableId(item);
    registerMutation(stableId);
    setItems((prev) =>
      prev.map((i) => getItemStableId(i) === stableId ? { ...i, due_date: newDueDate } : i)
    );
    try {
      await updateActionItemDueDateForItem(item, newDueDate);
    } catch (err) {
      if (mountedRef.current) {
        setItems((prev) =>
          prev.map((i) => getItemStableId(i) === stableId ? { ...i, due_date: item.due_date } : i)
        );
        setError(String(err?.message || "Failed to update due date"));
      }
    } finally {
      unregisterMutation(stableId);
    }
  }, [setItems, registerMutation, unregisterMutation]);

  const handlePinnedChange = useCallback(async (item, nextPinned) => {
    const stableId = getItemStableId(item);
    const previousPinned = Boolean(item?.pinned);
    const previousIsAging = Boolean(item?.is_aging);
    const previousStatus = String(item?.status || "pending");
    const previousAutoArchived = Boolean(item?.auto_archived);
    // Starring an archived task restores it in one step so Aging → Archived
    // can "bring it back and skip aging" without a separate Restore click.
    const shouldRestoreFromArchive =
      Boolean(nextPinned) && previousStatus === STATUS_IGNORE;
    registerMutation(stableId);
    skipNextKeyboardFocusScrollRef.current = true;
    setItems((prev) =>
      prev.map((i) =>
        getItemStableId(i) === stableId
          ? {
              ...i,
              pinned: Boolean(nextPinned),
              // Starring exempts from aging immediately; unstarring clears the flag until refresh.
              is_aging: nextPinned ? false : previousIsAging,
              ...(shouldRestoreFromArchive
                ? { status: "pending", auto_archived: false }
                : {}),
            }
          : i
      )
    );
    try {
      if (shouldRestoreFromArchive) {
        await updateActionItemStatusForItem(item, "pending", {
          pinned: true,
        });
      } else {
        await updateActionItemPinnedForItem(item, nextPinned);
      }
      // Sync derived fields (is_aging) while this row is still mutation-protected.
      await refreshActionItemsData({ silent: true }).catch(() => {});
    } catch (err) {
      if (mountedRef.current) {
        setItems((prev) =>
          prev.map((i) =>
            getItemStableId(i) === stableId
              ? {
                  ...i,
                  pinned: previousPinned,
                  is_aging: previousIsAging,
                  status: previousStatus,
                  auto_archived: previousAutoArchived,
                }
              : i
          )
        );
        setError(String(err?.message || "Failed to update star"));
      }
    } finally {
      unregisterMutation(stableId);
    }
  }, [refreshActionItemsData, setItems, registerMutation, unregisterMutation]);

  const handleTitleChange = useCallback(async (item, newTitle) => {
    const stableId = getItemStableId(item);
    const previousTitle = String(item.action_title || "").trim();
    registerMutation(stableId);
    setUpdatingKey(stableId);
    setItems((prev) =>
      prev.map((i) =>
        getItemStableId(i) === stableId ? { ...i, action_title: newTitle } : i
      )
    );
    try {
      await updateActionItemTitleForItem(item, newTitle);
    } catch (err) {
      if (mountedRef.current) {
        setItems((prev) =>
          prev.map((i) =>
            getItemStableId(i) === stableId ? { ...i, action_title: previousTitle } : i
          )
        );
        setError(String(err?.message || "Failed to rename task"));
      }
    } finally {
      if (mountedRef.current) setUpdatingKey(null);
      unregisterMutation(stableId);
    }
  }, [setItems, registerMutation, unregisterMutation]);

  const handleNoteChange = useCallback(async (item, newNote) => {
    const stableId = getItemStableId(item);
    const previousNote = String(item.action_item || "").trim();
    registerMutation(stableId);
    setUpdatingKey(stableId);
    setItems((prev) =>
      prev.map((i) =>
        getItemStableId(i) === stableId ? { ...i, action_item: newNote } : i
      )
    );
    try {
      await updateActionItemNoteForItem(item, newNote);
    } catch (err) {
      if (mountedRef.current) {
        setItems((prev) =>
          prev.map((i) =>
            getItemStableId(i) === stableId ? { ...i, action_item: previousNote } : i
          )
        );
        setError(String(err?.message || "Failed to update note"));
      }
    } finally {
      if (mountedRef.current) setUpdatingKey(null);
      unregisterMutation(stableId);
    }
  }, [setItems, registerMutation, unregisterMutation]);

  const reconcileSidebarSelectionRef = useRef(() => {});

  const handleDeleteItem = useCallback(async (item) => {
    const stableId = getItemStableId(item);
    const ok = window.confirm("Permanently delete this archived task? This cannot be undone.");
    if (!ok) return false;

    reconcileSidebarSelectionRef.current(item);
    registerMutation(stableId);
    setUpdatingKey(stableId);
    let removedItem = null;
    setItems((prev) => {
      removedItem = prev.find((i) => getItemStableId(i) === stableId) || null;
      return prev.filter((i) => getItemStableId(i) !== stableId);
    });
    try {
      await updateActionItemStatusForItem(item, STATUS_DELETED);
    } catch (err) {
      if (mountedRef.current && removedItem) {
        setItems((prev) => insertActionItemInViewSort(prev, removedItem, listSortOrder));
        setError(String(err?.message || "Failed to delete"));
      }
      return false;
    } finally {
      if (mountedRef.current) setUpdatingKey(null);
      unregisterMutation(stableId);
    }
    return true;
  }, [listSortOrder, setItems, registerMutation, unregisterMutation]);

  const handleClearArchived = useCallback(async () => {
    const filterId = String(
      new URLSearchParams(location.search).get("workflow") || ""
    ).trim();
    const targets = filterItemsByWorkflowQuery(items, filterId).filter(
      (i) => i.status === STATUS_IGNORE
    );
    if (!targets.length || clearingArchived) return;

    const ok = window.confirm(
      `Permanently delete ${targets.length} archived task(s)? This cannot be undone.`
    );
    if (!ok) return;

    const targetIds = new Set(targets.map(getItemStableId));
    setClearingArchived(true);
    setError("");
    const previousItems = items;
    setItems((prev) => prev.filter((i) => !targetIds.has(getItemStableId(i))));
    try {
      await Promise.all(
        targets.map((item) => updateActionItemStatusForItem(item, STATUS_DELETED))
      );
    } catch (err) {
      if (mountedRef.current) {
        setItems(previousItems);
        setError(String(err?.message || "Failed to delete archived tasks"));
      }
    } finally {
      if (mountedRef.current) setClearingArchived(false);
    }
  }, [clearingArchived, items, location.search, setItems]);

  /* ── Citation sidebar: activate a specific message (load its content) ── */
  const activateMessage = useCallback((workflowId, msg) => {
    const id = String(msg.message_id || "");
    const sourceItemId = activeSourceItemIdRef.current;
    setActiveMessageId(id);
    setContentCache((prev) => {
      if (prev[id] || !msg.gcs_url) return prev;
      const next = { ...prev, [id]: { loading: true, error: "", content: "" } };
      getRunInputMessageContent(workflowId, msg.gcs_url)
        .then((res) => {
          if (!mountedRef.current) return;
          const entry = {
            loading: false,
            error: "",
            content: String(res?.content || ""),
          };
          if (activeSourceItemIdRef.current === sourceItemId) {
            setContentCache((c) => ({
              ...c,
              [id]: entry,
            }));
          }
          if (sourceItemId) {
            const cached = sourceSessionCacheRef.current.get(sourceItemId);
            if (cached?.sidebarSourceLoaded) {
              sourceSessionCacheRef.current.set(sourceItemId, {
                ...cached,
                contentCache: { ...cached.contentCache, [id]: entry },
              });
            }
          }
        })
        .catch((err) => {
          if (!mountedRef.current) return;
          const entry = {
            loading: false,
            error: String(err?.message || "Failed"),
            content: "",
          };
          if (activeSourceItemIdRef.current === sourceItemId) {
            setContentCache((c) => ({
              ...c,
              [id]: entry,
            }));
          }
          if (sourceItemId) {
            const cached = sourceSessionCacheRef.current.get(sourceItemId);
            if (cached?.sidebarSourceLoaded) {
              sourceSessionCacheRef.current.set(sourceItemId, {
                ...cached,
                contentCache: { ...cached.contentCache, [id]: entry },
              });
            }
          }
        });
      return next;
    });
  }, []);

  const applySidebarSourceSnapshot = useCallback((snapshot) => {
    const state = snapshot ? cloneSidebarSourceSnapshot(snapshot) : EMPTY_SIDEBAR_SOURCE_STATE;
    setSidebarSourceLoaded(state.sidebarSourceLoaded);
    setSidebarWorkflowId(state.sidebarWorkflowId);
    setInputMessages(state.inputMessages);
    setActiveMessageId(state.activeMessageId);
    setActiveSourceSnippet(state.activeSourceSnippet);
    setContentCache(state.contentCache);
    setMessagesLoading(state.messagesLoading);
  }, []);

  useEffect(() => {
    sourceStateSnapshotRef.current = cloneSidebarSourceSnapshot({
      sidebarSourceLoaded,
      sidebarWorkflowId,
      inputMessages,
      activeMessageId,
      activeSourceSnippet,
      contentCache,
      messagesLoading,
    });
  }, [
    sidebarSourceLoaded,
    sidebarWorkflowId,
    inputMessages,
    activeMessageId,
    activeSourceSnippet,
    contentCache,
    messagesLoading,
  ]);

  useEffect(() => {
    const nextId = detailItem ? getItemStableId(detailItem) : null;
    const prevId = prevDetailItemIdRef.current;

    if (prevId && prevId !== nextId) {
      const snapshot = sourceStateSnapshotRef.current;
      if (snapshot.sidebarSourceLoaded) {
        sourceSessionCacheRef.current.set(prevId, cloneSidebarSourceSnapshot(snapshot));
      }
    }

    if (nextId !== prevId) {
      activeSourceItemIdRef.current = nextId;
      if (nextId) {
        applySidebarSourceSnapshot(sourceSessionCacheRef.current.get(nextId));
      } else {
        applySidebarSourceSnapshot(null);
      }
    }

    prevDetailItemIdRef.current = nextId;
  }, [detailItem, applySidebarSourceSnapshot]);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    setDetailItem(null);
  }, []);

  const handleOpenDetails = useCallback(
    (item, options = {}) => {
      const allowToggle = options.allowToggle !== false;
      const itemStableId = getItemStableId(item);
      if (
        allowToggle &&
        sidebarOpen &&
        detailItem &&
        getItemStableId(detailItem) === itemStableId
      ) {
        closeSidebar();
        return;
      }
      if (
        sidebarOpen &&
        detailItem &&
        getItemStableId(detailItem) === itemStableId
      ) {
        return;
      }
      setDetailItem(item);
      setSidebarOpen(true);
    },
    [sidebarOpen, detailItem, closeSidebar]
  );

  useEffect(() => {
    if (!isLandingDemo) return;
    const focusKey = String(landingDemoFocusItemKey || "").trim();
    if (!focusKey) {
      closeSidebar();
      return;
    }
    const match = items.find((entry) => String(entry?.key || "").trim() === focusKey);
    if (!match) return;
    handleOpenDetails(match, { allowToggle: false });
    // Demo tour drives focus by key; ignore list mutations so Manage clicks stay open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandingDemo, landingDemoFocusItemKey]);

  const loadSourceMessages = useCallback(async (item, options = {}) => {
    const workflowId = String(item.workflow_id || "");
    const fileId = String(item.file_id || "");
    const override = options.sourceSnippetOverride;
    const snippet = String(
      override != null && String(override).trim() !== ""
        ? override
        : item.source_snippet || ""
    );
    const pageUrl = resolveActionItemPageUrl(item);
    const sourceGcsUrl = String(item.source_gcs_url || "").trim();

    setSidebarWorkflowId(workflowId);
    // "Read transcript" opens the full text with an empty search box; only the
    // source-link popover ("Open in source") jumps to a quote.
    setActiveSourceSnippet(options.skipSnippet ? "" : snippet);

    // Manual items with a stored source document take priority: fetch the
    // real GCS body (the full pasted transcript, or the captured note/page
    // selection) and let `snippet` just seed the in-panel search/highlight —
    // same mechanism as connector-sourced messages. A snippet alone is NOT
    // "the original source" once it's a short grounded quote pulled from a
    // much larger transcript (legacy bulk-imported items may still have this shape).
    if (isManualActionItem(item) && sourceGcsUrl) {
      const messageId = fileId || "__capture__";
      const syntheticMsg = {
        message_id: messageId,
        gcs_url: sourceGcsUrl,
        subject: pageUrl ? "Captured from page" : "Captured selection",
        sent_time: String(item.message_sent_at || "").trim() || String(item.created_at || "").trim() || null,
        // Show exact whitespace/line breaks as pasted -- no markdown paragraph reflow.
        verbatim: true,
      };
      setInputMessages([syntheticMsg]);
      setSidebarSourceLoaded(true);
      activateMessage(workflowId, syntheticMsg);
      return;
    }

    // No source document yet (e.g. an optimistic item still persisting) —
    // show the snippet/page-link as a lightweight stand-in.
    if (isManualActionItem(item) && (pageUrl || snippet)) {
      const messageId = fileId || "__capture__";
      setActiveMessageId(messageId);
      setInputMessages([
        {
          message_id: messageId,
          subject: pageUrl ? "Captured from page" : "Captured selection",
          verbatim: true,
        },
      ]);
      const contentLines = [];
      if (snippet) contentLines.push(snippet);
      if (pageUrl) contentLines.push(`[Open source page](${pageUrl})`);
      setContentCache({
        [messageId]: {
          loading: false,
          error: "",
          content: contentLines.join("\n\n") || "No selection text saved for this item.",
        },
      });
      setSidebarSourceLoaded(true);
      return;
    }

    setActiveMessageId("");
    setInputMessages([]);
    setContentCache({});
    setSidebarSourceLoaded(true);

    // Non-manual (connector-synced) items, or a manual item somehow lacking
    // both a document and a snippet: unchanged from before -- item.source_gcs_url
    // is the primary path for most connector items (not just an edge-case
    // fallback), so this stays unrestricted by item type.
    if (sourceGcsUrl) {
      const syntheticSubject =
        resolveActionItemMeetingSubject(item) ||
        String(item.title || "").trim() ||
        String(item.action_title || "").trim() ||
        "Meeting source";
      const syntheticSentTime =
        String(item.message_sent_at || "").trim() ||
        String(item.created_at || "").trim() ||
        null;
      const syntheticMsg = {
        message_id: fileId,
        gcs_url: sourceGcsUrl,
        subject: syntheticSubject,
        sent_time: syntheticSentTime,
      };
      setInputMessages([syntheticMsg]);
      activateMessage(workflowId, syntheticMsg);
      return;
    }

    if (!workflowId || !fileId) return;

    setMessagesLoading(true);
    try {
      const msgs = await getRunInputMessages(workflowId, { sourceMessageIds: [fileId] });
      if (!mountedRef.current) return;
      const list = Array.isArray(msgs) ? [...msgs].sort(compareRunInputMessagesById) : [];
      setInputMessages(list);
      // Auto-expand the first (most relevant) message
      if (list[0]) activateMessage(workflowId, list[0]);
    } catch (_) {
      // Non-fatal: sidebar shows empty state
    } finally {
      if (mountedRef.current) setMessagesLoading(false);
    }
  }, [activateMessage]);

  /**
   * Task row link icon → open the details panel with the original source loaded and
   * ``snippet`` highlighted. Reuses the deep-link "pending source load" path, which
   * waits for the details item to switch before loading (loading earlier is wiped).
   *
   * @example
   * handleOpenSourceQuote(item, "I'll send the deck by Friday");
   * // right panel opens on `item`, source shown with that quote highlighted
   */
  const handleOpenSourceQuote = useCallback(
    (item, snippet = "") => {
      const alreadyOpen =
        sidebarOpen && detailItem && getItemStableId(detailItem) === getItemStableId(item);
      if (alreadyOpen) {
        void loadSourceMessages(item, { sourceSnippetOverride: snippet });
        return;
      }
      pendingSourceLoadRef.current = { item, sourceSnippet: snippet };
      handleOpenDetails(item, { allowToggle: false });
    },
    [detailItem, handleOpenDetails, loadSourceMessages, sidebarOpen]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const meetingFromLabel = String(params.get("meeting_from_label") || "").trim();
    const sourceSnippet = String(params.get("source_snippet") || "").trim();
    const actionItemText = String(params.get("action_item") || "").trim();
    const fileId = String(params.get("file_id") || "").trim();
    const hasDeepLink =
      Boolean(meetingFromLabel) || Boolean(sourceSnippet) || Boolean(actionItemText) || Boolean(fileId);
    if (!hasDeepLink) return;
    if (items.length === 0) return;
    if (handledDeepLinkSearchRef.current === location.search) return;
    const workflowFilter = String(params.get("workflow") || "").trim();
    const pool = filterItemsByWorkflowQuery(items, workflowFilter);
    const matchedItem = resolveDeepLinkItem(pool, actionItemText, meetingFromLabel, fileId);
    handledDeepLinkSearchRef.current = location.search;
    if (matchedItem) {
      // Queue the citation load until after the details-item effect resets
      // source state for the newly opened task. Loading in this same turn is
      // wiped by that reset, so the tab shows the task without original source.
      pendingSourceLoadRef.current = {
        item: matchedItem,
        sourceSnippet,
      };
      handleOpenDetails(matchedItem, { allowToggle: false });
    }
  }, [location.search, items, handleOpenDetails]);

  useEffect(() => {
    const nextId = detailItem ? getItemStableId(detailItem) : null;
    const pending = pendingSourceLoadRef.current;
    if (!pendingSourceLoadMatchesDetail(pending, nextId)) return;
    pendingSourceLoadRef.current = null;
    void loadSourceMessages(pending.item, {
      sourceSnippetOverride: pending.sourceSnippet,
    });
  }, [detailItem, loadSourceMessages]);

  const handleToggleMessage = useCallback((msg) => {
    const id = String(msg.message_id || "");
    if (activeMessageId === id) {
      setActiveMessageId("");
      setActiveSourceSnippet("");
    } else {
      activateMessage(sidebarWorkflowId, msg);
    }
  }, [activeMessageId, sidebarWorkflowId, activateMessage]);

  /** Leave the inline transcript and return to the task details. */
  const handleHideSource = useCallback(() => {
    setSidebarSourceLoaded(false);
    setActiveMessageId("");
    setActiveSourceSnippet("");
  }, []);

  const handleSidebarDeleteItem = useCallback(
    async (item) => handleDeleteItem(item),
    [handleDeleteItem]
  );

  /* ── Derived state ── */
  const workflowFilterId = String(
    new URLSearchParams(location.search).get("workflow") || ""
  ).trim();
  const categoryFilterId = String(
    new URLSearchParams(location.search).get("category") || ""
  ).trim();
  const assistFilterId = isAiReadyAssistFilterActive(location.search)
    ? ASSIST_FILTER_AI_READY
    : "";
  const isAgingFilterActive = isAgingFilterActiveFromSearch(location.search);
  const isStarredFilterActive = isStarredFilterActiveFromSearch(location.search);
  const assistSinceAtMs = parseAssistFilterSinceAtMs(location.search);
  const meetingFilterId = String(
    new URLSearchParams(location.search).get(ACTION_ITEMS_MEETING_FILTER_QUERY_PARAM) || ""
  ).trim();
  const meetingExcludedWorkflowIds = String(
    new URLSearchParams(location.search).get(ACTION_ITEMS_MEETING_EXCLUDE_QUERY_PARAM) || ""
  )
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  const setupSource = new URLSearchParams(location.search).get("source") === "setup";
  const isManualWorkflowFilter =
    workflowFilterId === MANUAL_WORKFLOW_FILTER_ID ||
    workflowFilterId === CAPTURE_WORKFLOW_ID;
  const workflowScopedItems = filterItemsByWorkflowQuery(items, workflowFilterId);
  const categoryFilteredItems = filterItemsByCategoryFilter(
    workflowScopedItems,
    categoryFilterId
  );
  const agingFilteredItems = filterItemsByAgingFlag(
    categoryFilteredItems,
    isAgingFilterActive
  );
  const starredFilteredItems = filterItemsByStarred(agingFilteredItems, isStarredFilterActive);
  const assistFilteredItems = filterItemsByAssistFilter(starredFilteredItems, assistFilterId, {
    sinceAtMs: assistSinceAtMs,
  });
  // Meeting scope spans every source (Zoom, Granola, ...), so it's normally matched by
  // a fuzzy time window rather than an exact timestamp — see filterItemsByMeetingWindow.
  // Captured/pasted-transcript items are the exception: every item from one paste already
  // shares the exact same meeting_from_label (stamped once per paste at creation time),
  // so there's no cross-source clock skew to tolerate. Applying the normal window here would
  // wrongly merge two different transcripts pasted within 15 minutes of each other.
  const isCapturedMeetingFilter = Boolean(
    meetingFilterId &&
      items.some(
        (it) =>
          String(it.meeting_from_label || "").trim() === meetingFilterId &&
          isManualActionItem(it)
      )
  );
  const meetingWindowItems = meetingFilterId
    ? filterItemsByMeetingWindow(assistFilteredItems, meetingFilterId, {
        windowMinutes: isCapturedMeetingFilter ? 0 : ACTION_ITEMS_MEETING_WINDOW_MINUTES,
      })
    : null;
  const meetingScopedItems = meetingWindowItems
    ? filterItemsExcludingWorkflowIds(meetingWindowItems, meetingExcludedWorkflowIds)
    : assistFilteredItems;
  const scopedItems = filterItemsByEventDateRange(meetingScopedItems, eventDateFrom, eventDateTo);
  const meetingSourceOptions = meetingWindowItems
    ? buildMeetingSourceOptions(meetingScopedItems)
    : [];
  const meetingFilterLabel = meetingFilterId
    ? formatMeetingLabelForDisplay(meetingFilterId)
    : "";
  // Only surface a source subtext when one source is left — once there are several,
  // ActionItemsActiveFilterBar nests them as removable pills inside the meeting chip instead.
  const meetingSourceLabel =
    meetingSourceOptions.length === 1 ? meetingSourceOptions[0].name : "";

  const headerSourceFilterOptions = useMemo(() => {
    const options = [{ value: "", label: "All tasks" }];
    if (sourceNav?.hasManualItems) {
      options.push({
        value: MANUAL_WORKFLOW_FILTER_ID,
        label: `Added by you (${sourceNav.manualCount ?? 0})`,
      });
    }
    (sourceNav?.workflowEntries || []).forEach(({ id, name, count }) => {
      options.push({ value: id, label: `${name} (${count})` });
    });
    return options;
  }, [sourceNav]);

  const headerCategoryFilterOptions = useMemo(
    () => buildActionItemsCategoryFilterOptions(workflowScopedItems, categoryCatalog),
    [categoryCatalog, workflowScopedItems]
  );

  const headerFiltersActive =
    Boolean(workflowFilterId) ||
    Boolean(categoryFilterId) ||
    Boolean(assistFilterId) ||
    isAgingFilterActive ||
    isStarredFilterActive ||
    eventDateFilterVisible;
  const selectedWorkflow = useMemo(
    () =>
      workflows.find(
        (workflow) => String(workflow?.workflow_id || "").trim() === workflowFilterId
      ) || null,
    [workflows, workflowFilterId]
  );
  const hasFirstRunWorkflowsInProgress = useMemo(
    () =>
      workflows.some((workflow) =>
        isWorkflowInFirstRunSetup(workflow, sessionStableWorkflowIds)
      ),
    [workflows, sessionStableWorkflowIds]
  );
  const firstRunInProgressWorkflowIds = useMemo(
    () =>
      new Set(
        workflows
          .filter((workflow) => isWorkflowInFirstRunSetup(workflow, sessionStableWorkflowIds))
          .map((workflow) => String(workflow?.workflow_id || "").trim())
          .filter(Boolean)
      ),
    [workflows, sessionStableWorkflowIds]
  );
  const isSelectedWorkflowFirstRunInProgress = useMemo(
    () =>
      Boolean(selectedWorkflow) &&
      isWorkflowInFirstRunSetup(selectedWorkflow, sessionStableWorkflowIds),
    [selectedWorkflow, sessionStableWorkflowIds]
  );
  const setupAtMs = Number(new URLSearchParams(location.search).get("setupAt") || 0);
  const setupGraceStartMs = Number.isFinite(setupAtMs) && setupAtMs > 0 ? setupAtMs : 0;
  const nowMs = Date.now();
  const isWithinSetupLoadingGraceWindow =
    setupSource &&
    workflowFilterId &&
    setupGraceStartMs > 0 &&
    nowMs - setupGraceStartMs < SETUP_LOADING_GRACE_MS;
  const selectedFirstRunCompleted = isWorkflowFirstRunComplete(selectedWorkflow);
  const selectedFirstRunState = String(selectedWorkflow?.first_run_state || "")
    .trim()
    .toUpperCase();

  useEffect(() => {
    const scopeKey = `${workflowFilterId}::${isManualWorkflowFilter ? "manual" : "digest"}`;
    const digestCount = isManualWorkflowFilter
      ? 0
      : workflowFilterId
        ? workflowScopedItems.length
        : items.filter((item) => !isManualActionItem(item)).length;

    if (scopeKey !== prevStableDigestScopeRef.current) {
      prevStableDigestScopeRef.current = scopeKey;
      prevDigestCountRef.current = -1;
      setStableDigestPollCount(0);
      return;
    }

    if (digestCount <= 0) {
      prevDigestCountRef.current = digestCount;
      setStableDigestPollCount(0);
      return;
    }

    if (digestCount === prevDigestCountRef.current) {
      setStableDigestPollCount((count) => count + 1);
    } else {
      setStableDigestPollCount(0);
    }
    prevDigestCountRef.current = digestCount;
  }, [
    items,
    workflowScopedItems.length,
    workflowFilterId,
    isManualWorkflowFilter,
    lastFetchedAt,
  ]);

  const firstRunDigestOutputStable =
    stableDigestPollCount >= STABLE_DIGEST_POLLS_FOR_COMPLETE &&
    (workflowFilterId
      ? workflowScopedItems.length > 0 && selectedFirstRunState === "TRANSFORM_RUNNING"
      : items.some((item) => !isManualActionItem(item)) &&
        workflows.some(
          (workflow) =>
            String(workflow?.first_run_state || "").trim().toUpperCase() ===
            "TRANSFORM_RUNNING"
        ));

  useEffect(() => {
    const hasCompletedFirstRun = workflows.some(isWorkflowFirstRunComplete);
    if (!firstRunDigestOutputStable && !hasCompletedFirstRun) return;
    setSessionStableWorkflowIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      workflows.forEach((workflow) => {
        const workflowId = String(workflow?.workflow_id || "").trim();
        if (!workflowId || next.has(workflowId)) return;
        const firstRunState = String(workflow?.first_run_state || "").trim().toUpperCase();
        const shouldMarkStable =
          isWorkflowFirstRunComplete(workflow) ||
          (firstRunDigestOutputStable && firstRunState === "TRANSFORM_RUNNING");
        if (!shouldMarkStable) return;
        persistFirstRunStableWorkflowId(workflowId);
        next.add(workflowId);
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [firstRunDigestOutputStable, workflows]);

  const isSessionFirstRunStable =
    Boolean(workflowFilterId) && sessionStableWorkflowIds.has(workflowFilterId);
  const hasDigestItemsInScope = workflowFilterId
    ? workflowScopedItems.length > 0
    : items.some((item) => !isManualActionItem(item));

  const showScopedFirstRunSpinner =
    Boolean(workflowFilterId) &&
    !isManualWorkflowFilter &&
    !selectedFirstRunCompleted &&
    !firstRunDigestOutputStable &&
    !isSessionFirstRunStable &&
    (isSelectedWorkflowFirstRunInProgress ||
      (isWithinSetupLoadingGraceWindow && !selectedWorkflow));
  const showGlobalFirstRunSpinner =
    !workflowFilterId &&
    !firstRunDigestOutputStable &&
    (hasFirstRunWorkflowsInProgress ||
      (setupSource &&
        setupGraceStartMs > 0 &&
        nowMs - setupGraceStartMs < SETUP_LOADING_GRACE_MS &&
        workflows.length === 0));
  const hasVisibleItemsForActiveFilter =
    activeFilter === "done"
      ? workflowScopedItems.some((item) => item.status === STATUS_DONE)
      : activeFilter === "ignored"
        ? workflowScopedItems.some((item) => item.status === STATUS_IGNORE)
        : workflowScopedItems.some((item) => item.status === STATUS_PENDING);
  const showFirstRunInProgressSpinner =
    showScopedFirstRunSpinner || showGlobalFirstRunSpinner;
  const showFirstRunSpinnerInView =
    showFirstRunInProgressSpinner &&
    (hasVisibleItemsForActiveFilter || !hasDigestItemsInScope);
  // Global view: only count items from connectors still in first-run setup, so
  // already-synced connectors don't inflate "found so far".
  const firstRunFetchedCount = workflowFilterId
    ? workflowScopedItems.length
    : items.filter(
        (item) =>
          !isManualActionItem(item) &&
          firstRunInProgressWorkflowIds.has(String(item.workflow_id || "").trim())
      ).length;
  const hasPartialFirstRunItems = showFirstRunSpinnerInView && firstRunFetchedCount > 0;

  const firstPullLookbackDays = useMemo(() => {
    const primaryWorkflow = workflows.find((workflow) =>
      String(workflow?.workflow_id || "").trim()
    );
    return primaryWorkflow
      ? resolveWorkflowLookbackDays(primaryWorkflow, DEFAULT_LOOKBACK_DAYS)
      : DEFAULT_LOOKBACK_DAYS;
  }, [workflows]);

  const firstPullWorkflowId = useMemo(() => {
    const workflow = workflows.find((entry) => String(entry?.workflow_id || "").trim());
    return String(workflow?.workflow_id || "").trim();
  }, [workflows]);

  const firstPullReadyToFinish =
    items.some((item) => !isManualActionItem(item)) ||
    workflows.some(isWorkflowFirstRunComplete);

  const handleFirstPullFinished = useCallback(() => {
    persistFirstPullFromOnboardingSeen();
    setFirstPullDismissed(true);
    navigate(ACTION_ITEMS_LIST_PATH, { replace: true });
  }, [navigate]);

  const showFirstPullScreen = showFirstPullEntry && !firstPullDismissed && !manualActionItemsMode;

  // Once per page visit. The endpoint advances the server-side "last seen" watermark, so
  // asking again on every list refresh (each poll, each route change) got an empty
  // "nothing new" answer the second time, which hid the banner a moment after it showed
  // and shifted the whole list up.
  const catchUpSettledRef = useRef(false);
  const catchUpReady =
    !showFirstPullScreen && !manualActionItemsMode && !itemsLoading && lastFetchedAt != null;
  useEffect(() => {
    if (!catchUpReady || catchUpSettledRef.current) return undefined;
    let cancelled = false;
    getActionItemsCatchUpSummary()
      .then((summary) => {
        if (cancelled) return;
        catchUpSettledRef.current = true;
        const summaryId = String(summary?.summary_id || "").trim();
        const dismissedId = readCatchUpDismissedSummaryId();
        if (summaryId && summaryId === dismissedId) {
          setCatchUpSummary(null);
          return;
        }
        if (!summary?.should_show || Number(summary?.new_count) <= 0) {
          setCatchUpSummary(null);
          return;
        }
        setCatchUpSummary(summary);
        setCatchUpHidden(false);
      })
      .catch(() => {
        if (!cancelled) {
          catchUpSettledRef.current = true;
          setCatchUpSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catchUpReady]);

  const handleDismissCatchUp = useCallback(async () => {
    const summaryId = String(catchUpSummary?.summary_id || "").trim();
    setCatchUpHidden(true);
    setCatchUpSummary(null);
    if (summaryId) {
      persistCatchUpDismissedSummaryId(summaryId);
    }
    try {
      await acknowledgeActionItemsCatchUp();
    } catch {
      // Banner is already hidden locally.
    }
  }, [catchUpSummary]);

  const handleReviewCatchUpAutomatable = useCallback(async () => {
    const sinceAt = String(catchUpSummary?.since_at || "").trim();
    const summaryId = String(catchUpSummary?.summary_id || "").trim();
    setCatchUpHidden(true);
    setCatchUpSummary(null);
    if (summaryId) {
      persistCatchUpDismissedSummaryId(summaryId);
    }
    try {
      await acknowledgeActionItemsCatchUp();
    } catch {
      // Continue to filtered view even if ack fails.
    }
    navigate(
      buildCatchUpAutomatablePath(sinceAt, {
        workflow: workflowFilterId,
        category: categoryFilterId,
      })
    );
  }, [catchUpSummary, categoryFilterId, navigate, workflowFilterId]);

  const showCatchUpBanner =
    Boolean(catchUpSummary) && !catchUpHidden && !showFirstPullScreen;

  useEffect(() => {
    if (!showFirstPullScreen) return undefined;
    const id = setInterval(() => {
      if (!document.hidden) {
        refreshActionItemsData({ silent: true });
      }
    }, FIRST_RUN_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshActionItemsData, showFirstPullScreen]);

  const handleClearActiveFilters = useCallback(() => {
    navigate(buildActionItemsListPath());
  }, [navigate]);

  const buildFilterPath = useCallback(
    (overrides = {}) => {
      const {
        workflow = workflowFilterId,
        category = categoryFilterId,
        assist = assistFilterId,
        aging = isAgingFilterActive,
        starred = isStarredFilterActive,
        meeting = meetingFilterId,
        meetingExclude = meetingExcludedWorkflowIds,
        sinceAt = Number.isFinite(assistSinceAtMs)
          ? new Date(assistSinceAtMs).toISOString()
          : "",
        ...rest
      } = overrides;
      return buildActionItemsListPath({
        workflow,
        category,
        assist,
        aging,
        starred,
        meeting,
        meetingExclude,
        sinceAt,
        ...rest,
      });
    },
    [
      assistFilterId,
      assistSinceAtMs,
      categoryFilterId,
      isAgingFilterActive,
      isStarredFilterActive,
      meetingExcludedWorkflowIds,
      meetingFilterId,
      workflowFilterId,
    ]
  );

  const handleFilterByMeeting = useCallback(
    (item) => {
      const meetingFromLabel = String(item?.meeting_from_label || "").trim();
      if (!meetingFromLabel) return;
      navigate(buildFilterPath({ meeting: meetingFromLabel, meetingExclude: [] }));
    },
    [buildFilterPath, navigate]
  );

  const handleFilterBySource = useCallback(
    (item) => {
      const sourceFilterId = resolveActionItemSourceFilterId(item);
      if (!sourceFilterId) return;
      navigate(buildFilterPath({ workflow: sourceFilterId }));
    },
    [buildFilterPath, navigate]
  );

  const handleRemoveMeetingSource = useCallback(
    (workflowId) => {
      if (!meetingFilterId) return;
      const nextExcluded = [
        ...meetingExcludedWorkflowIds,
        String(workflowId || "").trim(),
      ];
      navigate(
        buildFilterPath({
          meeting: meetingFilterId,
          meetingExclude: nextExcluded,
        })
      );
    },
    [buildFilterPath, meetingExcludedWorkflowIds, meetingFilterId, navigate]
  );

  const handleSelectWorkflowFilter = useCallback(
    (workflowId) => {
      const nextWorkflow = String(workflowId || "").trim();
      navigate(buildFilterPath({ workflow: nextWorkflow }));
    },
    [buildFilterPath, navigate]
  );

  const handleSelectCategoryFilter = useCallback(
    (categoryId) => {
      const normalized = String(categoryId || "").trim();
      const category =
        !normalized || normalized === CATEGORY_FILTER_ALL ? "" : normalized;
      navigate(buildFilterPath({ category }));
    },
    [buildFilterPath, navigate]
  );

  const handleToggleAiReadyFilter = useCallback(() => {
    const nextAssist = assistFilterId ? "" : ASSIST_FILTER_AI_READY;
    navigate(
      buildFilterPath({
        assist: nextAssist,
        sinceAt:
          nextAssist && Number.isFinite(assistSinceAtMs)
            ? new Date(assistSinceAtMs).toISOString()
            : "",
      })
    );
  }, [assistFilterId, assistSinceAtMs, buildFilterPath, navigate]);

  const handleToggleAgingFilter = useCallback(() => {
    navigate(buildFilterPath({ aging: !isAgingFilterActive }));
  }, [buildFilterPath, isAgingFilterActive, navigate]);

  const handleToggleStarredFilter = useCallback(() => {
    navigate(buildFilterPath({ starred: !isStarredFilterActive }));
  }, [buildFilterPath, isStarredFilterActive, navigate]);

  const handleClearStarredFilter = useCallback(() => {
    navigate(buildFilterPath({ starred: false }));
  }, [buildFilterPath, navigate]);

  const handleClearAgingFilter = useCallback(() => {
    navigate(buildFilterPath({ aging: false }));
  }, [buildFilterPath, navigate]);

  const handleClearSourceFilter = useCallback(
    () => handleSelectWorkflowFilter(""),
    [handleSelectWorkflowFilter]
  );

  const handleClearCategoryFilter = useCallback(
    () => handleSelectCategoryFilter(""),
    [handleSelectCategoryFilter]
  );

  const handleClearMeetingFilter = useCallback(() => {
    navigate(buildFilterPath({ meeting: "", meetingExclude: [] }));
  }, [buildFilterPath, navigate]);

  useEffect(() => {
    if (!showFirstRunInProgressSpinner) return undefined;
    const id = setInterval(() => {
      if (!document.hidden) refreshActionItemsData({ silent: true });
    }, FIRST_RUN_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [showFirstRunInProgressSpinner, refreshActionItemsData]);

  const pendingItems = scopedItems.filter((i) => i.status === STATUS_PENDING);
  const doneItems = scopedItems.filter((i) => i.status === STATUS_DONE);
  const ignoredItems = scopedItems.filter((i) => i.status === STATUS_IGNORE);
  const agingPendingCount = pendingItems.filter((i) => Boolean(i?.is_aging)).length;

  const handleDismissAgingBanner = useCallback(() => {
    persistAgingBannerDismissedCount(agingPendingCount);
    setAgingBannerHidden(true);
  }, [agingPendingCount]);

  const handleReviewAgingBanner = useCallback(() => {
    persistAgingBannerDismissedCount(agingPendingCount);
    setAgingBannerHidden(true);
    navigate(buildFilterPath({ aging: true }));
  }, [agingPendingCount, buildFilterPath, navigate]);

  useEffect(() => {
    if (agingPendingCount <= 0) {
      setAgingBannerHidden(false);
      return;
    }
    const dismissedCount = readAgingBannerDismissedCount();
    setAgingBannerHidden(agingPendingCount <= dismissedCount);
  }, [agingPendingCount]);

  const workflowNameById = useMemo(() => {
    const map = new Map();
    workflows.forEach((workflow) => {
      const id = String(workflow?.workflow_id || "").trim();
      if (!id || id === CAPTURE_WORKFLOW_ID) return;
      const name = String(workflow?.name || "").trim();
      if (name) map.set(id, name);
    });
    items.forEach((item) => {
      const id = String(item.workflow_id || "").trim();
      if (!id || id === CAPTURE_WORKFLOW_ID) return;
      const name = String(item.workflow_name || "").trim();
      if (name) map.set(id, name);
    });
    return map;
  }, [items, workflows]);

  const workflowTypeById = useMemo(() => {
    const map = new Map();
    workflows.forEach((workflow) => {
      const id = String(workflow?.workflow_id || "").trim();
      const type = String(workflow?.workflow_type || "").trim();
      if (id && type) map.set(id, type);
    });
    items.forEach((item) => {
      const id = String(item.workflow_id || "").trim();
      const type = String(item.workflow_type || "").trim();
      if (id && type) map.set(id, type);
    });
    return map;
  }, [items, workflows]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  // Empty search must not depend on aiResultsByItem: storing a draft would
  // rebuild visibleItems and the sidebar-selection effect can treat that as
  // "item left the list" and jump to the neighbor.
  const aiResultsForSearch = normalizedQuery ? aiResultsByItem : null;
  const filterItems = useCallback((list) => {
    if (!normalizedQuery) return list;
    const resultsMap = aiResultsForSearch || {};
    return list.filter((i) => {
      const stableId = getItemStableId(i);
      const aiResult = resultsMap[stableId] || resultsMap[i.action_item] || null;
      const draftText = String(aiResult?.draft || "").toLowerCase();
      return (
        String(i.action_item || "").toLowerCase().includes(normalizedQuery) ||
        String(i.workflow_name || "").toLowerCase().includes(normalizedQuery) ||
        String(i.meeting_from_label || "").toLowerCase().includes(normalizedQuery) ||
        String(i.source_snippet || "").toLowerCase().includes(normalizedQuery) ||
        draftText.includes(normalizedQuery)
      );
    });
  }, [normalizedQuery, aiResultsForSearch]);

  const statusFilteredItems =
    activeFilter === "done"
      ? doneItems
      : activeFilter === "ignored"
        ? ignoredItems
        : pendingItems;

  const visibleItems = useMemo(() => {
    const filtered = filterItems(statusFilteredItems);
    return sortActionItemsForView(filtered, listSortOrder);
  }, [statusFilteredItems, filterItems, listSortOrder]);

  /**
   * Move keyboard focus and record the focused item id in the same turn.
   * Sort remaps look up that id; a stale id snaps J/K back one row.
   *
   * @param {number} nextIndex Target index in `visibleItems`, or -1 to clear.
   * @param {{ skipScroll?: boolean, fromKeyboard?: boolean }} [options]
   * @returns {number} The index that was applied.
   * @example
   * applyKeyboardFocusIndex(1, { fromKeyboard: true });
   * // keyboardFocusIndex becomes 1 and itemIdRef matches visibleItems[1]
   */
  const applyKeyboardFocusIndex = useCallback(
    (nextIndex, { skipScroll = false, fromKeyboard = false } = {}) => {
      const applied = syncKeyboardFocusRefs({
        index: nextIndex,
        visibleItems,
        getStableId: getItemStableId,
        indexRef: keyboardFocusIndexRef,
        itemIdRef: keyboardFocusItemIdRef,
      });
      if (skipScroll) skipNextKeyboardFocusScrollRef.current = true;
      if (fromKeyboard) setKeyboardNavActive(true);
      setKeyboardFocusIndex(applied);
      return applied;
    },
    [visibleItems]
  );

  const reconcileSidebarSelection = useCallback(
    (changedItem, { previousVisibleItems = visibleItems, afterListChange = false } = {}) => {
      if (!sidebarOpen || !detailItem) return;
      const changedId = getItemStableId(changedItem);
      if (changedId !== getItemStableId(detailItem)) return;

      const fallback = afterListChange
        ? resolveStillVisibleSidebarFallback(
            previousVisibleItems,
            visibleItems,
            getItemStableId,
            changedId
          )
        : resolveSidebarFallbackItem(previousVisibleItems, getItemStableId, changedId);

      if (fallback) {
        const fallbackId = getItemStableId(fallback);
        const liveFallback = afterListChange
          ? visibleItems.find((entry) => getItemStableId(entry) === fallbackId) || null
          : fallback;
        if (liveFallback) {
          setDetailItem(liveFallback);
          return;
        }
      }
      if (afterListChange && visibleItems.length > 0) {
        setDetailItem(visibleItems[0]);
        return;
      }
      closeSidebar();
    },
    [sidebarOpen, detailItem, visibleItems, closeSidebar]
  );

  useEffect(() => {
    reconcileSidebarSelectionRef.current = reconcileSidebarSelection;
  }, [reconcileSidebarSelection]);

  const prevVisibleItemsRef = useRef(visibleItems);
  useEffect(() => {
    if (!sidebarOpen || !detailItem) {
      prevVisibleItemsRef.current = visibleItems;
      return;
    }
    const detailId = getItemStableId(detailItem);
    const detailSidebarKey = getItemSidebarKey(detailItem);
    const stillVisible = visibleItems.some(
      (entry) =>
        getItemSidebarKey(entry) === detailSidebarKey ||
        getItemStableId(entry) === detailId
    );
    if (!stillVisible) {
      const runState = performerRunStateByItem[detailId];
      if (runState?.loading) {
        prevVisibleItemsRef.current = visibleItems;
        return;
      }
      reconcileSidebarSelection(detailItem, {
        previousVisibleItems: prevVisibleItemsRef.current,
        afterListChange: true,
      });
    }
    prevVisibleItemsRef.current = visibleItems;
  }, [sidebarOpen, detailItem, visibleItems, reconcileSidebarSelection, performerRunStateByItem]);

  // Undo toast after Done / Archive. `items` are the rows as they were before the change.
  const [undoToast, setUndoToast] = useState(null);
  const undoToastIdRef = useRef(0);
  const dismissUndoToast = useCallback(() => setUndoToast(null), []);

  const showUndoToast = useCallback(
    (changedItems, toStatus) => {
      if (isLandingDemo || changedItems.length === 0) return;
      const verb = toStatus === STATUS_DONE ? "marked done" : "archived";
      undoToastIdRef.current += 1;
      setUndoToast({
        id: undoToastIdRef.current,
        message:
          changedItems.length === 1
            ? `${verb.charAt(0).toUpperCase()}${verb.slice(1)}`
            : `${changedItems.length} tasks ${verb}`,
        items: changedItems,
        toStatus,
      });
    },
    [isLandingDemo]
  );

  const handleUndoStatusChange = useCallback(() => {
    if (!undoToast) return;
    const { items: changedItems, toStatus } = undoToast;
    setUndoToast(null);
    setError("");
    void runOptimisticBulkActionItemStatusChange({
      targets: changedItems.map((entry) => ({ ...entry, status: toStatus })),
      newStatus: changedItems[0].status,
      ...optimisticStatusOptions,
    });
  }, [optimisticStatusOptions, undoToast]);

  const handleVisibleStatusChange = useCallback(
    (item, newStatus) => {
      if (itemLeavesActiveFilterView(activeFilter, newStatus)) {
        reconcileSidebarSelection(item, { previousVisibleItems: visibleItems });
      }
      handleStatusChange(item, newStatus);
      if ((newStatus === STATUS_DONE || newStatus === STATUS_IGNORE) && item?.status !== newStatus) {
        showUndoToast([item], newStatus);
      }
    },
    [activeFilter, visibleItems, reconcileSidebarSelection, handleStatusChange, showUndoToast]
  );

  const landingDemoItemsRef = useRef(items);
  landingDemoItemsRef.current = items;
  const handlePinnedChangeRef = useRef(handlePinnedChange);
  handlePinnedChangeRef.current = handlePinnedChange;
  const handleVisibleStatusChangeRef = useRef(handleVisibleStatusChange);
  handleVisibleStatusChangeRef.current = handleVisibleStatusChange;
  const landingDemoInboxActionKey = landingDemoInboxAction
    ? `${landingDemoInboxAction.type}:${landingDemoInboxAction.itemKey}:${landingDemoInboxAction.delayMs || 0}`
    : "";

  useEffect(() => {
    if (!isLandingDemo) return undefined;
    const nextFilter = String(landingDemoInboxFilter || "").trim();
    if (!nextFilter) return undefined;
    setActiveFilter(nextFilter);
    return undefined;
  }, [isLandingDemo, landingDemoInboxFilter]);

  useEffect(() => {
    if (!isLandingDemo) return undefined;
    const targetKey = String(
      landingDemoHoverItemKey || landingDemoFocusItemKey || ""
    ).trim();
    if (!targetKey) return undefined;
    const index = visibleItems.findIndex(
      (entry) => String(entry?.key || "").trim() === targetKey
    );
    if (index < 0) return undefined;
    const targetId = getItemStableId(visibleItems[index]);
    if (keyboardFocusItemIdRef.current && keyboardFocusItemIdRef.current === targetId) {
      // Same focused task after a reorder — keep the highlight, don't jump the list.
      if (index !== keyboardFocusIndexRef.current) {
        applyKeyboardFocusIndex(index, { skipScroll: true });
      }
      return undefined;
    }
    applyKeyboardFocusIndex(index);
    return undefined;
  }, [applyKeyboardFocusIndex, isLandingDemo, landingDemoHoverItemKey, landingDemoFocusItemKey, visibleItems]);

  useEffect(() => {
    if (!isLandingDemo || !landingDemoInboxActionKey) return undefined;
    const action = landingDemoInboxAction;
    if (!action?.type || !action?.itemKey) return undefined;
    const delay = Math.max(0, Number(action.delayMs) || 0);
    const timerId = window.setTimeout(() => {
      const match = landingDemoItemsRef.current.find(
        (entry) => String(entry?.key || "").trim() === String(action.itemKey || "").trim()
      );
      if (!match) return;
      if (action.type === "pin") {
        handlePinnedChangeRef.current(match, true);
        return;
      }
      if (action.type === "complete") {
        handleVisibleStatusChangeRef.current(match, STATUS_DONE);
        return;
      }
      if (action.type === "archive") {
        handleVisibleStatusChangeRef.current(match, STATUS_IGNORE);
      }
    }, delay);
    return () => window.clearTimeout(timerId);
    // Step identity is landingDemoInboxActionKey; handlers/items are read from refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandingDemo, landingDemoInboxActionKey]);

  useEffect(() => {
    const focusId = keyboardFocusItemIdRef.current;
    const nextIndex = remapKeyboardFocusAfterListChange({
      focusId,
      currentIndex: keyboardFocusIndexRef.current,
      visibleItems,
      getStableId: getItemStableId,
    });
    if (nextIndex === keyboardFocusIndexRef.current) return;
    const itemStillPresent =
      Boolean(focusId) &&
      visibleItems.some((entry) => getItemStableId(entry) === focusId);
    applyKeyboardFocusIndex(nextIndex, { skipScroll: itemStillPresent });
  }, [applyKeyboardFocusIndex, visibleItems]);

  useEffect(() => {
    if (keyboardFocusIndex < 0 || keyboardFocusIndex >= visibleItems.length) {
      keyboardFocusItemIdRef.current = null;
      skipNextKeyboardFocusScrollRef.current = false;
      return undefined;
    }
    if (skipNextKeyboardFocusScrollRef.current) {
      skipNextKeyboardFocusScrollRef.current = false;
      return undefined;
    }
    const stableId =
      keyboardFocusItemIdRef.current ||
      getItemStableId(visibleItems[keyboardFocusIndex]);
    const escapedId =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(stableId)
        : String(stableId).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const root = splitLayoutRef.current || document;
    const row = root.querySelector?.(
      `.action-item-row[data-action-item-id="${escapedId}"]`
    );
    if (!row) return undefined;
    if (isLandingDemo) {
      const frameId = window.requestAnimationFrame(() => {
        scrollRowIntoActionItemsBody(row);
      });
      const timerId = window.setTimeout(() => {
        scrollRowIntoActionItemsBody(row);
      }, 160);
      return () => {
        window.cancelAnimationFrame(frameId);
        window.clearTimeout(timerId);
      };
    }
    scrollRowIntoActionItemsBody(row);
    return undefined;
    // splitLayoutRef is a stable ref (declared later in this component via
    // destructuring); referencing it here in the deps array would hit the
    // TDZ before its declaration runs, so it's intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandingDemo, keyboardFocusIndex]);

  useEffect(() => {
    const resolveFocusedItem = () => {
      const index = keyboardFocusIndexRef.current;
      if (index >= 0 && index < visibleItems.length) {
        return visibleItems[index];
      }
      return null;
    };

    const ensureFocusIndex = () => {
      if (visibleItems.length === 0) return -1;
      const current = keyboardFocusIndexRef.current;
      if (current >= 0 && current < visibleItems.length) return current;
      applyKeyboardFocusIndex(0, { fromKeyboard: true });
      return 0;
    };

    const resolveFocusIndex = () => {
      const current = keyboardFocusIndexRef.current;
      if (current >= 0 && current < visibleItems.length) return current;
      if (sidebarOpen && detailItem) {
        const detailId = getItemStableId(detailItem);
        const fromSidebar = visibleItems.findIndex(
          (entry) => getItemStableId(entry) === detailId
        );
        if (fromSidebar >= 0) return fromSidebar;
      }
      return -1;
    };

    const moveKeyboardFocus = (delta) => {
      if (visibleItems.length === 0) return;
      const prev = resolveFocusIndex();
      const next =
        prev < 0
          ? delta > 0
            ? 0
            : visibleItems.length - 1
          : Math.max(0, Math.min(prev + delta, visibleItems.length - 1));
      applyKeyboardFocusIndex(next, { fromKeyboard: true });
      if (!sidebarOpen) return;
      const item = visibleItems[next];
      if (!item) return;
      handleOpenDetails(item, { allowToggle: false });
    };

    const handleKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.isComposing) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (document.querySelector(".settings-info-tip__popover")) return;

      const match = (id) =>
        eventMatchesBinding(event, shortcutBindings[id]);

      const target = event.target;
      // While the cursor is in any input/textarea/select/contenteditable,
      // never fire list shortcuts — including when the field is still empty
      // (so typing the first letter of a task/search query works).
      const isTyping = isTypingTarget(target);

      if (match("sidebar.close")) {
        if (isTyping) return;
        if (sidebarOpen) {
          event.preventDefault();
          closeSidebar();
        }
        return;
      }

      if (isTyping) return;

      if (match("search.focus")) {
        event.preventDefault();
        searchControlsRef.current?.toggle?.();
        return;
      }

      if (match("task.add")) {
        event.preventDefault();
        manualAddInputRef.current?.focus?.();
        return;
      }

      if (match("task.voice")) {
        event.preventDefault();
        addFormControlsRef.current?.toggleVoice?.();
        return;
      }

      if (match("settings.open")) {
        event.preventDefault();
        handleOpenSettings(ACTION_ITEMS_SETTINGS_TAB_GENERAL);
        return;
      }

      if (match("settings.connectors.open")) {
        event.preventDefault();
        handleOpenSettings(ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS);
        return;
      }

      if (match("task.next")) {
        event.preventDefault();
        moveKeyboardFocus(1);
        return;
      }

      if (match("task.previous")) {
        event.preventDefault();
        moveKeyboardFocus(-1);
        return;
      }

      if (match("task.open")) {
        const index = ensureFocusIndex();
        if (index < 0) return;
        const item = visibleItems[index];
        if (!item) return;
        event.preventDefault();
        handleOpenDetails(item);
        return;
      }

      if (match("task.complete")) {
        const index = ensureFocusIndex();
        if (index < 0) return;
        const item = visibleItems[index] || resolveFocusedItem();
        if (!item) return;
        event.preventDefault();
        const isDone = item.status === STATUS_DONE;
        const isIgnored = item.status === STATUS_IGNORE;
        handleVisibleStatusChange(
          item,
          isDone || isIgnored ? STATUS_PENDING : STATUS_DONE
        );
        return;
      }

      if (match("task.archive")) {
        const index = ensureFocusIndex();
        if (index < 0) return;
        const item = visibleItems[index] || resolveFocusedItem();
        if (!item) return;
        event.preventDefault();
        if (item.status === STATUS_IGNORE) {
          handleVisibleStatusChange(item, STATUS_PENDING);
        } else {
          handleVisibleStatusChange(item, STATUS_IGNORE);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    applyKeyboardFocusIndex,
    closeSidebar,
    detailItem,
    handleOpenDetails,
    handleOpenSettings,
    handleVisibleStatusChange,
    shortcutBindings,
    sidebarOpen,
    visibleItems,
  ]);

  const bulk = useActionItemBulkSelect(
    ENABLE_ACTION_ITEMS_BULK_SELECT && activeFilter === "pending" ? visibleItems : [],
    handleVisibleStatusChange,
    getItemStableId,
    optimisticStatusOptions
  );
  const {
    clearSelection,
    selectedCount,
    allVisibleSelected,
    handleToggleSelectAll,
    handleBulkArchive: runBulkArchive,
    handleBulkDone: runBulkDone,
    getSelectedItems,
    toggleItemSelection,
    isItemSelected,
  } = bulk;

  const withUndoToast = useCallback(
    (runBulk, toStatus) => () => {
      const changedItems = getSelectedItems().filter((entry) => entry.status !== toStatus);
      runBulk();
      showUndoToast(changedItems, toStatus);
    },
    [getSelectedItems, showUndoToast]
  );
  const handleBulkDone = useMemo(
    () => withUndoToast(runBulkDone, STATUS_DONE),
    [runBulkDone, withUndoToast]
  );
  const handleBulkArchive = useMemo(
    () => withUndoToast(runBulkArchive, STATUS_IGNORE),
    [runBulkArchive, withUndoToast]
  );

  const bulkSelectEnabled =
    ENABLE_ACTION_ITEMS_BULK_SELECT && activeFilter === "pending";

  const detailItemLive = useMemo(() => {
    if (!detailItem) return null;
    const sidebarKey = getItemSidebarKey(detailItem);
    const bySidebarKey = items.find((entry) => getItemSidebarKey(entry) === sidebarKey);
    if (bySidebarKey) return bySidebarKey;
    const stableId = getItemStableId(detailItem);
    return items.find((entry) => getItemStableId(entry) === stableId) || detailItem;
  }, [detailItem, items]);

  const meetingAddAnchor = useMemo(() => {
    if (!meetingFilterId) return null;
    return resolveMeetingAddAnchor({
      items: assistFilteredItems,
      meetingFilterId,
      preferredItem: detailItemLive,
    });
  }, [assistFilteredItems, detailItemLive, meetingFilterId]);

  const meetingLinkDismissed = meetingFilterId
    ? meetingLinkDismissedKeys.has(meetingFilterId)
    : false;

  const meetingAddContext = useMemo(() => {
    if (!meetingFilterId || meetingLinkDismissed || !meetingAddAnchor) return null;
    return buildMeetingAddContext(meetingAddAnchor);
  }, [meetingAddAnchor, meetingFilterId, meetingLinkDismissed]);

  meetingAddContextRef.current = meetingAddContext
    ? { linkToMeeting: true, ...meetingAddContext }
    : { linkToMeeting: false };

  const meetingAddHintLabel = useMemo(
    () => formatMeetingAddContextHintLabel({ meetingFilterId }),
    [meetingFilterId]
  );

  const dismissMeetingAddContext = useCallback(() => {
    if (!meetingFilterId) return;
    setMeetingLinkDismissedKeys((previous) => {
      const next = new Set(previous);
      next.add(meetingFilterId);
      return next;
    });
  }, [meetingFilterId]);

  const enableMeetingAddContext = useCallback(() => {
    if (!meetingFilterId) return;
    setMeetingLinkDismissedKeys((previous) => {
      if (!previous.has(meetingFilterId)) return previous;
      const next = new Set(previous);
      next.delete(meetingFilterId);
      return next;
    });
  }, [meetingFilterId]);

  const showMeetingAddReenable =
    Boolean(meetingFilterId && meetingAddAnchor && meetingLinkDismissed);

  const showDockedDetailsSidebar =
    sidebarOpen && detailItemLive && detailsSidebarDocked;
  const showInlineDetailsSidebar =
    sidebarOpen && detailItemLive && !detailsSidebarDocked;
  const {
    layoutRef: splitLayoutRef,
    splitStyle: splitLayoutStyle,
    isResizing: splitResizing,
    sidebarPercent: splitSidebarPercent,
    onDividerPointerDown: onSplitDividerPointerDown,
  } = useActionItemsSplitResize(showDockedDetailsSidebar);
  const detailItemStableId = detailItemLive ? getItemStableId(detailItemLive) : "";
  const detailItemStableIdRef = useRef(detailItemStableId);
  detailItemStableIdRef.current = detailItemStableId;
  // Separate from detailItemStableId (used for real-identity lookups like AI results): this
  // is only for the sidebar's React `key`, so a manual item's optimistic → reconciled swap
  // re-renders in place instead of remounting and dropping an in-progress edit.
  const detailSidebarReactKey = detailItemLive ? getItemSidebarKey(detailItemLive) : "";

  const [sidebarServerCalendarEvent, setSidebarServerCalendarEvent] = useState(null);
  useEffect(() => {
    if (!detailItemLive?.key || !detailItemLive?.due_date) {
      setSidebarServerCalendarEvent(null);
      return;
    }
    const isPersonalCat = !["meet", "call", "follow", "opportunit"].some(
      (kw) => String(detailItemLive.action_category || "").toLowerCase().includes(kw)
    );
    if (!isPersonalCat) return;
    const cachedEvent = peekCachedCalendarEvent(detailItemLive.key);
    if (cachedEvent !== undefined) {
      setSidebarServerCalendarEvent(cachedEvent);
      return undefined;
    }
    let cancelled = false;
    getCalendarEventForItem(detailItemLive.key).then((event) => {
      if (!cancelled) setSidebarServerCalendarEvent(event || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [detailItemLive?.key, detailItemLive?.due_date, detailItemLive?.action_category]);

  const detailItemPersistedAiResult =
    detailItemLive &&
    overlayPerformerResultPatch(
      aiResultsByItem[detailItemStableId] || aiResultsByItem[detailItemLive.action_item] || null,
      resolveAiResultPatch(aiResultPatchesByItem, detailItemLive, detailItemStableId)
    );

  const handleRowRunStateChange = useCallback((stableId, state) => {
    if (!stableId || !state) return;
    if (state.loading && !state.result) {
      setAiResultPatchesByItem((prev) => {
        if (!prev[stableId]) return prev;
        const next = { ...prev };
        delete next[stableId];
        return next;
      });
    }
    const startingFreshRun = Boolean(state.loading) && !state.result;
    setPerformerRunStateByItem((prev) => ({
      ...prev,
      [stableId]: nextRowRunEntry(prev[stableId], state),
    }));
    if (startingFreshRun) {
      setAiResultsByItem((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key === stableId || key === `${stableId}:crm`) {
            delete next[key];
            changed = true;
            return;
          }
          const row = next[key];
          if (row && String(row.client_item_id || "").trim() === stableId) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } else if (state.result) {
      setAiResultsByItem((prev) => ({
        ...prev,
        [stableId]: state.result,
      }));
    }
    // A CRM side-effect can stop being proposed on retry (see useSingleItemPerformerRun),
    // and state.done still needs to clear a stale one from a previous attempt.
    if (state.crmSideEffect || state.done) {
      setAiResultsByItem((prev) => ({
        ...prev,
        [`${stableId}:crm`]: state.crmSideEffect || null,
      }));
    }
  }, []);

  const {
    result: detailRunResult,
    crmSideEffect: detailRunCrmSideEffect,
    progress: detailRunProgress,
    loading: detailRunLoading,
    error: detailRunError,
    dismissError: dismissDetailRunError,
    run: runDetailItemAi,
  } = useSingleItemPerformerRun(
    detailItemLive || {},
    detailItemLive?.workflow_id,
    0,
    detailItemPersistedAiResult?.gmail_draft_id,
    handleRowRunStateChange
  );
  // Persist sidebar run results into aiResultsByItem so the row badge survives the sidebar closing.
  // Only react to new results — not sidebar task switches — so a prior task's output is not copied.
  // Merge with any already-cached row: switching back re-emits the original in-session
  // run, which must not wipe Gmail send/edit patches stored after that run finished.
  useEffect(() => {
    if (!detailRunResult) return;
    const resultOwnerId = String(detailRunResult.client_item_id || "").trim();
    const stableId =
      resultOwnerId && !resultOwnerId.endsWith(":crm")
        ? resultOwnerId
        : detailItemStableIdRef.current;
    if (!stableId) return;
    setAiResultsByItem((prev) => {
      const existing = prev[stableId];
      return {
        ...prev,
        [stableId]: mergeLiveAndPersistedPerformerResult(detailRunResult, existing),
      };
    });
  }, [detailRunResult]);

  // Same, for a CRM side-effect proposed alongside the sidebar's own item. Clearing a
  // stale side-effect after a retry that stops proposing one is handleRowRunStateChange's
  // job (it also runs off this same hook instance) — this effect only ever persists a find.
  useEffect(() => {
    if (!detailRunCrmSideEffect) return;
    const stableId = detailItemStableIdRef.current;
    if (!stableId) return;
    setAiResultsByItem((prev) => ({
      ...prev,
      [`${stableId}:crm`]: detailRunCrmSideEffect,
    }));
  }, [detailRunCrmSideEffect]);

  const runDetailItemAiRef = useRef(runDetailItemAi);
  runDetailItemAiRef.current = runDetailItemAi;
  const detailItemLiveRef = useRef(detailItemLive);
  detailItemLiveRef.current = detailItemLive;

  const handleAiResultPatch = useCallback((patch) => {
    const item = detailItemLiveRef.current;
    if (!item || !patch || typeof patch !== "object") return;
    const stableId = getItemStableId(item);
    const actionItem = String(item.action_item || "").trim();
    setAiResultPatchesByItem((prev) => {
      if (!stableId) return prev;
      return {
        ...prev,
        [stableId]: { ...(prev[stableId] || {}), ...patch },
      };
    });
    setAiResultsByItem((prev) =>
      applyPerformerResultPatch(prev, { stableId, actionItem, patch })
    );
    if (!stableId) return;
    setPerformerRunStateByItem((prev) => {
      const existing = prev[stableId];
      if (!existing?.result) return prev;
      return {
        ...prev,
        [stableId]: {
          ...existing,
          result: { ...existing.result, ...patch },
        },
      };
    });
  }, []);

  const landingDemoPerformerActionKey = landingDemoPerformerAction
    ? `${landingDemoPerformerAction.itemKey}:${landingDemoPerformerAction.delayMs || 0}`
    : "";

  useEffect(() => {
    if (!isLandingDemo || !landingDemoPerformerActionKey) return undefined;
    const action = landingDemoPerformerAction;
    if (!action?.itemKey) return undefined;
    const delay = Math.max(0, Number(action.delayMs) || 0);
    const timerId = window.setTimeout(() => {
      const live = detailItemLiveRef.current;
      if (!live || String(live.key || "").trim() !== String(action.itemKey || "").trim()) {
        return;
      }
      void runDetailItemAiRef.current();
    }, delay);
    return () => window.clearTimeout(timerId);
    // Step identity is landingDemoPerformerActionKey; run/item are read from refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLandingDemo, landingDemoPerformerActionKey]);

  /**
   * Dismiss an AI Assist run error everywhere it could show (row hook state, sidebar
   * hook state). A new run starts clean, so a repeat failure shows again.
   *
   * @example
   * handleDismissRunError(stableId); // row + details panel both lose the message
   */
  const handleDismissRunError = useCallback(
    (stableId) => {
      if (!stableId) return;
      setPerformerRunStateByItem((prev) => dismissRowRunError(prev, stableId));
      if (stableId === detailItemStableIdRef.current) dismissDetailRunError();
    },
    [dismissDetailRunError]
  );

  const resolveItemRunState = useCallback(
    (item) => {
      const stableId = getItemStableId(item);
      const rowState = performerRunStateByItem[stableId] || {};
      const detailState =
        stableId === detailItemStableId
          ? {
              loading: detailRunLoading,
              progress: detailRunProgress,
              result: detailRunResult,
              crmSideEffect: detailRunCrmSideEffect,
              error: detailRunError,
              run: runDetailItemAi,
            }
          : {};
      return mergePerformerRunStates(rowState, detailState);
    },
    [
      performerRunStateByItem,
      detailItemStableId,
      detailRunLoading,
      detailRunProgress,
      detailRunResult,
      detailRunCrmSideEffect,
      detailRunError,
      runDetailItemAi,
    ]
  );

  const detailsSidebarProps = useMemo(() => {
    if (!detailItemLive) return null;
    const runState = resolveItemRunState(detailItemLive);
    const persistedAiResult =
      aiResultsByItem[detailItemStableId] ||
      aiResultsByItem[detailItemLive.action_item] ||
      null;
    const sidebarAiResult = withCachedCrmWrittenFields(
      overlayPerformerResultPatch(
        mergeLiveAndPersistedPerformerResult(runState.result, persistedAiResult),
        resolveAiResultPatch(aiResultPatchesByItem, detailItemLive, detailItemStableId)
      )
    );
    const persistedCrmSideEffect = aiResultsByItem[`${detailItemStableId}:crm`] || null;
    const sidebarCrmSideEffect = withCachedCrmWrittenFields(
      runState.crmSideEffect || persistedCrmSideEffect
    );
    const calendarPrefillUrl =
      sidebarAiResult?.type === "calendar"
        ? String(sidebarAiResult.calendar_prefill_url || "").trim() ||
          buildCalendarPrefillUrl({
            ...detailItemLive,
            action_item: String(sidebarAiResult.draft || detailItemLive.action_item || "").trim(),
            due_date: String(
              sidebarAiResult.suggested_due_date || detailItemLive.due_date || ""
            ).trim(),
            scheduled_start_at: sidebarAiResult.scheduled_start_at || null,
            scheduled_end_at: sidebarAiResult.scheduled_end_at || null,
          })
        : null;
    return {
      item: detailItemLive,
      workflowTypeById,
      aiResult: sidebarAiResult,
      crmSideEffectResult: sidebarCrmSideEffect,
      aiProgress: resolveItemActiveAiProgress({
        item: detailItemLive,
        singleRunProgress: runState.progress,
      }),
      gmailAuthUrl,
      gmailPerformerReady,
      onGmailPerformerReady: handleGmailPerformerReady,
      onRetryAi: runState.run,
      onAiResultPatch: handleAiResultPatch,
      retryLoading: runState.loading,
      retryError: runState.error || "",
      onDismissRetryError: () => handleDismissRunError(detailItemStableId),
      performerConfig,
      onOpenAiSettings: handleOpenAiAssistSettings,
      onFilterByMeeting: handleFilterByMeeting,
      onFilterBySource: handleFilterBySource,
      onClose: closeSidebar,
      onTitleChange: handleTitleChange,
      onNoteChange: handleNoteChange,
      onDueDateChange: handleDueDateChange,
      onCategoryChange: handleCategoryChange,
      categoryOptions: categoryCatalog,
      onAccountChange: handleAccountChange,
      accountSuggestions,
      onStatusChange: handleVisibleStatusChange,
      onDeleteItem: handleSidebarDeleteItem,
      showAiAssist: ENABLE_ACTION_ITEMS_AI,
      calendarPrefillUrl,
      serverCalendarEventUrl: sidebarServerCalendarEvent?.event_url || null,
      updating:
        updatingKey === getItemStableId(detailItemLive) ||
        clearingArchived,
      onViewSource: () => void loadSourceMessages(detailItemLive, { skipSnippet: true }),
      onHideSource: handleHideSource,
      sourceMessages: inputMessages,
      sourceMessagesLoading: messagesLoading,
      activeMessageId,
      activeSourceSnippet,
      contentCache,
      onToggleMessage: handleToggleMessage,
      showSourceMessages: sidebarSourceLoaded,
    };
  }, [
    detailItemLive,
    detailItemStableId,
    resolveItemRunState,
    workflowTypeById,
    aiResultsByItem,
    aiResultPatchesByItem,
    gmailAuthUrl,
    gmailPerformerReady,
    handleGmailPerformerReady,
    handleAiResultPatch,
    handleFilterByMeeting,
    handleFilterBySource,
    performerConfig,
    handleOpenAiAssistSettings,
    closeSidebar,
    handleTitleChange,
    handleNoteChange,
    handleDueDateChange,
    handleCategoryChange,
    categoryCatalog,
    handleAccountChange,
    accountSuggestions,
    handleVisibleStatusChange,
    handleSidebarDeleteItem,
    updatingKey,
    clearingArchived,
    loadSourceMessages,
    handleHideSource,
    inputMessages,
    messagesLoading,
    activeMessageId,
    activeSourceSnippet,
    contentCache,
    handleToggleMessage,
    sidebarSourceLoaded,
    sidebarServerCalendarEvent,
    handleDismissRunError,
  ]);

  const renderDetailsSidebar = (placement) => {
    if (!detailsSidebarProps) return null;
    const className =
      placement === "inline" ? "action-item-details-sidebar--inline" : "";
    return (
      <ActionItemDetailsSidebar
        key={detailSidebarReactKey}
        {...detailsSidebarProps}
        className={className}
      />
    );
  };

  useEffect(() => {
    // Same escape-the-clipped-frame issue as the keyboard-focus effect above:
    // skip this in the landing demo, whose fixed-height frame clips overflow
    // instead of scrolling internally.
    if (isLandingDemo) return;
    if (!showInlineDetailsSidebar || !inlineDetailsSidebarRef.current) return;
    inlineDetailsSidebarRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [isLandingDemo, showInlineDetailsSidebar, detailItemStableId]);

  useEffect(() => {
    if (activeFilter !== "pending") {
      clearSelection();
    }
  }, [activeFilter, clearSelection]);

  const landingDemoHighlightKey = isLandingDemo
    ? String(landingDemoFocusItemKey || landingDemoHoverItemKey || "").trim()
    : "";

  const renderActionItemRow = (item) => {
    const itemStableId = getItemStableId(item);
    const runState = resolveItemRunState(item);
    return (
    <ActionItemRow
      layout="full"
      item={item}
      workflowNameById={workflowNameById}
      workflowTypeById={workflowTypeById}
      onStatusChange={handleVisibleStatusChange}
      onDeleteItem={handleDeleteItem}
      onDueDateChange={handleDueDateChange}
      onPinnedChange={handlePinnedChange}
      onTitleChange={handleTitleChange}
      onNoteChange={handleNoteChange}
      updating={
        updatingKey === itemStableId ||
        clearingArchived
      }
      justAdded={recentlyAddedKeys.has(getItemSidebarKey(item))}
      onOpenDetails={handleOpenDetails}
      onOpenSourceQuote={isLandingDemo ? undefined : handleOpenSourceQuote}
      keyboardFocused={
        landingDemoHighlightKey
          ? String(item?.key || "").trim() === landingDemoHighlightKey
          : keyboardFocusIndex >= 0 &&
            visibleItems[keyboardFocusIndex] &&
            getItemStableId(visibleItems[keyboardFocusIndex]) === itemStableId
      }
      aiResult={withCachedCrmWrittenFields(
        overlayPerformerResultPatch(
          mergeLiveAndPersistedPerformerResult(
            runState.result,
            aiResultsByItem[itemStableId] || aiResultsByItem[item.action_item] || null
          ),
          resolveAiResultPatch(aiResultPatchesByItem, item, itemStableId)
        )
      )}
      crmSideEffectResult={withCachedCrmWrittenFields(
        runState.crmSideEffect || aiResultsByItem[`${itemStableId}:crm`] || null
      )}
      aiProgress={resolveItemActiveAiProgress({
        item,
        singleRunProgress: runState.progress,
      })}
      performerRunState={{
        loading: runState.loading,
        progress: runState.progress,
        result: runState.result,
        crmSideEffect: runState.crmSideEffect,
        error: runState.error || "",
      }}
      errorShownInDetails={
        Boolean(sidebarOpen) && Boolean(detailItemStableId) && itemStableId === detailItemStableId
      }
      onDismissRunError={handleDismissRunError}
      onRunStateChange={handleRowRunStateChange}
      gmailAuthUrl={gmailAuthUrl}
      gmailPerformerReady={gmailPerformerReady}
      onGmailPerformerReady={handleGmailPerformerReady}
      showCategoryLabel
      showWorkflowColumn={false}
      showAiAssist={ENABLE_ACTION_ITEMS_AI}
      performerConfig={performerConfig}
      onOpenAiSettings={handleOpenAiAssistSettings}
      rowSelectable={bulkSelectEnabled}
      rowSelected={bulkSelectEnabled && isItemSelected(item)}
      onRowSelectToggle={bulkSelectEnabled ? toggleItemSelection : undefined}
      inlineActionsIconOnly={
        /* Collapse Archive / AI labels to icons when the list column is tight:
         * overlay/narrow viewport, or the docked details panel is open (~half width). */
        !detailsSidebarDocked || showDockedDetailsSidebar
      }
    />
    );
  };

  const renderVisibleListItem = (item) => {
    const itemSidebarKey = getItemSidebarKey(item);
    const showInlinePanel =
      showInlineDetailsSidebar && itemSidebarKey === detailSidebarReactKey;
    return (
      <React.Fragment key={itemSidebarKey}>
        {renderActionItemRow(item)}
        {showInlinePanel ? (
          <div
            ref={inlineDetailsSidebarRef}
            className="action-item-details-sidebar-inline-wrap"
          >
            {renderDetailsSidebar("inline")}
          </div>
        ) : null}
      </React.Fragment>
    );
  };

  const sessionArchivedView = !showArchivedTab && activeFilter === "ignored";

  const stats = useMemo(
    () =>
      buildActionItemsStatusStats({
        todoCount: pendingItems.length,
        doneCount: doneItems.length,
        archivedCount: ignoredItems.length,
        showArchivedTab,
        sessionArchivedView,
      }),
    [
      ignoredItems.length,
      pendingItems.length,
      doneItems.length,
      showArchivedTab,
      sessionArchivedView,
    ]
  );

  const hasFetchedActionItems = lastFetchedAt != null;
  const showListLoadingSpinner =
    items.length === 0 && (!hasFetchedActionItems || itemsLoading);
  const shouldRedirectToConnectSettings = shouldRedirectEmptyAccountToConnectOnboarding({
    hasFetchedActionItems,
    itemsLoading,
    itemsCount: items.length,
    workflowsCount: workflows.length,
    enableActionItemsAi: ENABLE_ACTION_ITEMS_AI,
    search: location.search,
  });

  useEffect(() => {
    if (items.length > 0) {
      persistPasteTranscriptHintDismissed();
      setShowPasteTranscriptHint(false);
      if (isPasteTranscriptHintDeepLink(location.search)) {
        const next = new URLSearchParams(location.search);
        next.delete(PASTE_TRANSCRIPT_HINT_QUERY_PARAM);
        const qs = next.toString();
        navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
      }
      return;
    }
    if (shouldRedirectToConnectSettings) return;
    if (!hasFetchedActionItems || itemsLoading) return;
    if (readPasteTranscriptHintDismissed()) return;
    setShowPasteTranscriptHint(true);
  }, [
    hasFetchedActionItems,
    items.length,
    itemsLoading,
    location.pathname,
    location.search,
    navigate,
    shouldRedirectToConnectSettings,
  ]);

  const listEmptyCopy = useMemo(() => {
    if (
      items.length === 0 &&
      workflows.length === 0 &&
      (manualActionItemsMode || isPasteTranscriptHintDeepLink(location.search))
    ) {
      return {
        title: "No action items yet",
        subtitle: "Add a task below or connect a source in settings.",
      };
    }
    if (scopedItems.length === 0 && workflowFilterId) {
      return isManualWorkflowFilter
        ? { title: "No items added by you yet." }
        : { title: "No tasks found for this topic." };
    }
    if (normalizedQuery) {
      return { title: "No tasks match your search." };
    }
    if (isStarredFilterActive) {
      return {
        title: "No starred tasks",
        subtitle: "Click the star on a task to mark it important.",
      };
    }
    if (isAgingFilterActive && activeFilter === "pending") {
      return {
        title: "Nothing's gone quiet",
        subtitle: "Tasks land here after 25+ days with no activity, before we archive them at 30.",
      };
    }
    if (isAgingFilterActive && activeFilter === "ignored") {
      return {
        title: "No auto-archived tasks",
        subtitle: "Tasks we archive after 30 quiet days show up here.",
      };
    }
    if (activeFilter === "pending") {
      return {
        title: "You're all caught up",
        subtitle: "New items will appear here when they're ready.",
      };
    }
    if (activeFilter === "done") {
      return {
        title: "No completed tasks yet",
        subtitle: "Check off items as you finish them.",
      };
    }
    return {
      title: "No archived tasks",
      subtitle: "Items you archive will appear here.",
    };
  }, [
    activeFilter,
    isAgingFilterActive,
    isStarredFilterActive,
    isManualWorkflowFilter,
    items.length,
    location.search,
    manualActionItemsMode,
    normalizedQuery,
    scopedItems.length,
    workflowFilterId,
    workflows.length,
  ]);

  const showAgingFilterExplainer =
    isAgingFilterActive &&
    (activeFilter === "pending" || activeFilter === "ignored") &&
    !showListLoadingSpinner;

  const showAgingBanner =
    agingPendingCount > 0 &&
    !agingBannerHidden &&
    !isAgingFilterActive &&
    activeFilter === "pending" &&
    !showFirstPullScreen &&
    !showListLoadingSpinner;

  // Single notice slot: usage_limit > catch_up > aging > credits_restored.
  const activeListNotice = resolveActionItemsListNotice({
    usageLimitReached: Boolean(aiCreditsUsage?.usageLimitReached),
    catchUpEligible: showCatchUpBanner,
    agingEligible: showAgingBanner,
    creditsRestoredEligible: creditsRestoredVisible,
  });

  if (showFirstPullScreen) {
    return (
      <ActionItemsFirstPullScreen
        lookbackDays={firstPullLookbackDays}
        workflowId={firstPullWorkflowId}
        readyToFinish={firstPullReadyToFinish}
        onFinished={handleFirstPullFinished}
      />
    );
  }

  return (
    <div
      ref={splitLayoutRef}
      className={`action-items-layout action-items-layout--global${
        showDockedDetailsSidebar ? " action-items-layout--split" : ""
      }${splitResizing ? " action-items-layout--split-resizing" : ""}`}
      style={splitLayoutStyle}
    >
      <div
        className={`action-items-wrap action-items-wrap--clean-rows action-items-wrap--no-workflow-col${
          ENABLE_ACTION_ITEMS_AI ? "" : " action-items-wrap--minimal"
        }${keyboardNavActive ? " action-items-wrap--keyboard-nav" : ""}`}
        onPointerMove={() => {
          if (keyboardNavActive) setKeyboardNavActive(false);
        }}
      >
        {error || itemsError ? (
          <div className="error-message">{error || itemsError}</div>
        ) : null}

        {/* ── Fixed top — never scrolls ── */}
        <div className="action-items-fixed-top">
          <div className="action-items-stats-row">
            <div className="action-items-stats action-items-stats--split">
              {stats.map(({ key, value, label, tier }) => (
                <button
                  key={key}
                  type="button"
                  data-stat-key={key}
                  className={`action-items-stat action-items-stat--${tier}${
                    activeFilter === key ? " action-items-stat--active" : ""
                  }`}
                  onClick={() => setActiveFilter(key)}
                >
                  <span className="action-items-stat__value" aria-label={`${label} count`}>
                    {value}
                  </span>
                  <span className="action-items-stat__label">{label}</span>
                </button>
              ))}
            </div>
            <div className="action-items-fixed-top__meta">
              <div
                className="action-items-fixed-top__actions"
                aria-label="Action items shortcuts"
              >
                {activeFilter === "ignored" && ignoredItems.length > 0 ? (
                  <button
                    type="button"
                    className="action-items-clear-archived-icon-btn"
                    onClick={() => void handleClearArchived()}
                    disabled={clearingArchived}
                    title="Permanently delete all archived tasks in this view"
                    aria-label={
                      clearingArchived ? "Deleting archived tasks" : "Delete all archived tasks"
                    }
                  >
                    {clearingArchived ? (
                      <span className="action-items-overflow-menu__spinner" aria-hidden="true" />
                    ) : (
                      <ActionItemDeleteIcon size={16} />
                    )}
                  </button>
                ) : null}
                <ActionItemsExpandableSearch
                  ref={searchControlsRef}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  expandOnHover={!isLandingDemo}
                  className={isLandingDemo ? "action-items-inline-search--landing-embed" : ""}
                />
                <ActionItemsFilterHoverMenu
                  sourceFilter={{
                    value: workflowFilterId,
                    options: headerSourceFilterOptions,
                    onChange: handleSelectWorkflowFilter,
                  }}
                  categoryFilter={
                    headerCategoryFilterOptions
                      ? {
                          value: categoryFilterId || CATEGORY_FILTER_ALL,
                          options: headerCategoryFilterOptions,
                          onChange: handleSelectCategoryFilter,
                        }
                      : null
                  }
                  aiReadyFilterActive={Boolean(assistFilterId)}
                  onToggleAiReadyFilter={handleToggleAiReadyFilter}
                  agingFilterActive={isAgingFilterActive}
                  onToggleAgingFilter={handleToggleAgingFilter}
                  starredFilterActive={isStarredFilterActive}
                  onToggleStarredFilter={handleToggleStarredFilter}
                  filtersActive={headerFiltersActive}
                  onActivateDateFilter={handleActivateEventDateFilter}
                  dateFilterActive={eventDateFilterVisible}
                />
                <ActionItemsSortHoverMenu
                  value={listSortOrder}
                  options={ACTION_ITEM_SORT_OPTIONS}
                  onChange={setListSortOrder}
                  sortActive={listSortOrder !== ACTION_ITEM_SORT_DEFAULT}
                />
                {!showArchivedTab ? (
                  <ActionItemsArchiveHoverMenu
                    archivedActive={activeFilter === "ignored"}
                    onEnterArchivedView={handleEnterArchivedView}
                    onExitArchivedView={handleExitArchivedView}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="action-items-toolbar-filters">
            <div className="action-items-toolbar-filters__track">
              <ActionItemsActiveFilterBar
                workflowFilterId={workflowFilterId}
                categoryFilterId={categoryFilterId}
                assistFilterId={assistFilterId}
                agingFilterActive={isAgingFilterActive}
                starredFilterActive={isStarredFilterActive}
                meetingLabel={meetingFilterLabel}
                meetingSourceLabel={meetingSourceLabel}
                meetingSources={meetingSourceOptions}
                sourceNav={sourceNav}
                onRemoveSource={handleClearSourceFilter}
                onRemoveCategory={handleClearCategoryFilter}
                onRemoveAssist={handleToggleAiReadyFilter}
                onRemoveAging={handleClearAgingFilter}
                onRemoveStarred={handleClearStarredFilter}
                onRemoveMeeting={handleClearMeetingFilter}
                onRemoveMeetingSource={handleRemoveMeetingSource}
                onClear={handleClearActiveFilters}
                className="action-items-active-filter--inline"
              />
              {eventDateFilterVisible ? (
                <ActionItemsEventDateFilter
                  from={eventDateFrom}
                  to={eventDateTo}
                  onChange={handleEventDateFilterChange}
                  onClear={handleClearEventDateFilter}
                  className="action-items-date-filter--inline"
                />
              ) : null}
            </div>
          </div>

          {ENABLE_ACTION_ITEMS_BULK_SELECT && selectedCount > 0 ? (
            <ActionItemBulkBar
              selectedCount={selectedCount}
              allVisibleSelected={allVisibleSelected}
              compact
              onToggleSelectAll={handleToggleSelectAll}
              onClear={clearSelection}
              onMarkDone={handleBulkDone}
              onArchive={handleBulkArchive}
            />
          ) : null}

          {activeListNotice === ACTION_ITEMS_LIST_NOTICE.CATCH_UP ? (
            <ActionItemsCatchUpBanner
              summary={catchUpSummary}
              onDismiss={handleDismissCatchUp}
              onReviewAutomatable={handleReviewCatchUpAutomatable}
            />
          ) : null}
          {activeListNotice === ACTION_ITEMS_LIST_NOTICE.AGING ? (
            <ActionItemsAgingBanner
              count={agingPendingCount}
              onDismiss={handleDismissAgingBanner}
              onReview={handleReviewAgingBanner}
            />
          ) : null}
          {activeListNotice === ACTION_ITEMS_LIST_NOTICE.USAGE_LIMIT ? (
            <UsageLimitBanner />
          ) : null}
          {activeListNotice === ACTION_ITEMS_LIST_NOTICE.CREDITS_RESTORED ? (
            <AiCreditsToppedUpBanner
              visible
              onDismiss={() => setCreditsRestoredVisible(false)}
            />
          ) : null}
        </div>

        <ActionItemCreateFeedback feedback={createFeedback} />
        <UndoToast
          toast={undoToast}
          onUndo={handleUndoStatusChange}
          onDismiss={dismissUndoToast}
        />

        <div className="action-items-scroll-body">
          {showAgingFilterExplainer ? (
            <p className="action-items-aging-explainer" role="note">
              {activeFilter === "ignored"
                ? "These tasks were auto-archived after 30 days quiet. Restore one to bring it back, or star it so it won't age out again."
                : "These tasks haven't come up in 25+ days. We'll archive them after 30 days unless you star them or set a due date."}
            </p>
          ) : null}
          {showListLoadingSpinner ? (
            <section className="action-items-section">
              <div className="action-items-first-run-loading" role="status" aria-live="polite">
                <InlineSpinner text="Loading action items…" />
              </div>
            </section>
          ) : visibleItems.length > 0 ? (
            <section className="action-items-section">
              {showFirstRunSpinnerInView ? (
                <div className="action-items-first-run-loading" role="status" aria-live="polite">
                  <InlineSpinner
                    text={
                      hasPartialFirstRunItems
                        ? `Syncing · ${firstRunFetchedCount} action item${
                            firstRunFetchedCount === 1 ? "" : "s"
                          } found so far`
                        : "Generating your first action items..."
                    }
                  />
                  <p className="action-items-first-run-loading__sub">
                    {hasPartialFirstRunItems
                      ? "More items will appear as the sync finishes."
                      : "Usually takes 1-3 minutes."}
                  </p>
                </div>
              ) : null}
              <div className="action-items-list">{visibleItems.map(renderVisibleListItem)}</div>
            </section>
          ) : showFirstRunSpinnerInView ? (
            <section className="action-items-section">
              <div className="action-items-first-run-loading" role="status" aria-live="polite">
                <InlineSpinner text="Generating your first action items..." />
                <p className="action-items-first-run-loading__sub">Usually takes 1-3 minutes.</p>
              </div>
            </section>
          ) : (
            <section className="action-items-section">
              <ActionItemsListEmptyState
                title={listEmptyCopy.title}
                subtitle={listEmptyCopy.subtitle}
              />
            </section>
          )}
        </div>

        <div className="action-items-bottom-add">
          {showPasteTranscriptHint && !meetingAddContext && !showMeetingAddReenable ? (
            <PasteTranscriptHint onDismiss={dismissPasteTranscriptHint} />
          ) : null}
          <GlobalAddActionItemForm
            inputRef={manualAddInputRef}
            controlsRef={addFormControlsRef}
            onSubmit={handleAddWithMeetingContext}
            onCancel={undefined}
            showCancel={false}
            pasteHintActive={showPasteTranscriptHint && !meetingAddContext && !showMeetingAddReenable}
            onPasteHintDismiss={dismissPasteTranscriptHint}
            meetingHintLinked={Boolean(meetingAddContext)}
            meetingHintUnlinked={showMeetingAddReenable}
            meetingHintLabel={
              meetingAddContext
                ? meetingAddHintLabel
                : showMeetingAddReenable
                  ? MEETING_ADD_UNLINKED_HINT_LABEL
                  : ""
            }
            onMeetingHintDismiss={dismissMeetingAddContext}
            onMeetingHintEnable={enableMeetingAddContext}
          />
        </div>
      </div>

      {showDockedDetailsSidebar ? (
        <>
          <div
            className="action-items-split-divider"
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={Math.round(splitSidebarPercent)}
            aria-valuemin={22}
            aria-valuemax={55}
            aria-label="Resize action item details panel"
            onPointerDown={onSplitDividerPointerDown}
          />
          {renderDetailsSidebar("docked")}
        </>
      ) : null}

    </div>
  );
};

export default GlobalActionItemsPage;
export { GlobalAddActionItemForm };
